import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Form,
  Input,
  Switch,
  InputNumber,
  Select,
  Button,
  Card,
  Row,
  Col,
  Upload,
  message,
  Alert,
  Divider,
  Tag,
  Layout,
  Space,
  Modal,
  Collapse,
  Steps,
  Segmented,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  UploadOutlined,
  WhatsAppOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppContext from "../../AppContext";
import TimeField from "../shared/TimeField";
import DateField, { formatDate } from "../shared/DateField";
import { formatDualDate } from "../../utils/eventDate";
import axios from "axios";
//import { BASE_URL, LANDING_PAGE_URL } from "../../Constants";
import { BASE_URL, LANDING_PAGE_URL } from "../../Constants";
import GateManagement from "./GateManagement";
import OrganizerManagement from "./OrganizerManagement";
import SessionManagement from "./SessionManagement";
import LocationPicker from "../company/LocationPicker";
import SpeakerManagement from "./SpeakerManagement";
import ExhibitorManagement from "./ExhibitorManagement";
import WhatsAppInvitationPreview from "./WhatsAppInvitationPreview";
import ThankYouMessagePreview from "./ThankYouMessagePreview";
import InvitationThemePicker from "./InvitationThemePicker";
import DigitalCardImageEditor from "./DigitalCardImageEditor";
import { getDefaultDescription } from "./descriptionTemplates";

// Tighter spacing for the Date & Time and Venue cards: less empty space, still not packed.
const COMPACT_CARD_CSS = `
  .fc-compact-card .ant-card-head { min-height: 0; padding: 0 16px; }
  .fc-compact-card .ant-card-head-title { padding: 10px 0; }
  .fc-compact-card .ant-card-body { padding: 8px 16px 12px; }
  .fc-compact-card .ant-form-item { margin-bottom: 12px; }
  .fc-compact-card .ant-form-item-label { padding-bottom: 4px; }
  .fc-compact-card .ant-form-item-explain, .fc-compact-card .ant-form-item-extra { min-height: 0; }
`;

const { TextArea } = Input;
const { Option } = Select;
const { Content } = Layout;
const { Panel } = Collapse;
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Default wording of the WhatsApp invitation (family_invitation_v1). These mirror the server's defaults
// (InvitationMessage.DefaultInvitedBy / WhatsAppText.DefaultClosingLine): the form shows them prefilled, and
// a value that still equals the default is saved as "empty" so the server keeps using its own default.
const DEFAULT_INVITED_BY = "العائلة";
const DEFAULT_CLOSING_TEXT = "نتطلع إلى حضوركم ومشاركتكم فرحتنا";
const DEFAULT_GUEST_TITLE = "الفاضل/الفاضلة"; // "Dear Mr./Ms." - suits men and women alike
const defaultInvitationText = (invitedBy) => `يتشرف ${(invitedBy || "").trim() || DEFAULT_INVITED_BY} بدعوتكم لحضور`;
// Mirrors the server's WhatsAppText.DefaultThankYouText (event_thank_you_v1, sent manually from
// Event Registrations to checked-in guests once the event has ended).
const DEFAULT_THANK_YOU_TEXT = "نشكركم على تشريفنا وحضوركم";

const isValidGuid = (value) => typeof value === "string" && GUID_REGEX.test(value);

// Native <input type="date"> / <input type="time"> just deal in plain "YYYY-MM-DD" /
// "HH:mm" strings, so all the date/time fields on this form carry those strings directly
// instead of moment/dayjs objects - this replaces antd's RangePicker/TimePicker/DatePicker,
// which pull in a large scrollable panel that scrolls poorly on mobile.
const isValidTimeStr = (value) => typeof value === "string" && /^\d{2}:\d{2}/.test(value);

// API responses can come back as a full ISO datetime or "HH:mm:ss" - trim to what the
// native inputs expect.
const toDateInputValue = (value) => (typeof value === "string" && value ? value.slice(0, 10) : null);
const toTimeInputValue = (value) => (typeof value === "string" && value ? value.slice(0, 5) : null);

// Event dates share one leading-calendar choice and one Hijri +/-1 correction, kept in the form
// as hidden fields. The correction is only offered on the start date; the end date follows it.
const EventDateInput = ({ showAdjustment, ...props }) => {
  const form = Form.useFormInstance();
  const calendar = Form.useWatch("calendarPreference", form);
  const adjustment = Form.useWatch("hijriAdjustmentDays", form) || 0;
  const { primaryColor } = useContext(AppContext);
  return (
    <DateField
      {...props}
      primaryColor={primaryColor}
      calendar={calendar}
      onCalendarChange={(next) => form.setFieldsValue({ calendarPreference: next })}
      adjustment={adjustment}
      showAdjustment={showAdjustment}
      onAdjustmentChange={(next) => form.setFieldsValue({ hijriAdjustmentDays: next })}
    />
  );
};

// Start date: choosing it also moves the end date to the same day, unless the end was already set
// to a later day on purpose (i.e. it differs from the start the user is moving away from).
const EventStartDateInput = ({ value, onChange, ...props }) => {
  const form = Form.useFormInstance();
  const handleChange = (next) => {
    const end = form.getFieldValue("endDate");
    if (next && (!end || end < next || end === value)) form.setFieldsValue({ endDate: next });
    onChange?.(next);
  };
  return <EventDateInput {...props} value={value} onChange={handleChange} />;
};

// End date: days before the start are disabled, and a value that ends up before it is pulled up.
const EventEndDateInput = ({ value, onChange, ...props }) => {
  const form = Form.useFormInstance();
  const startDate = Form.useWatch("startDate", form);
  useEffect(() => {
    if (value && startDate && value < startDate) onChange?.(startDate);
  }, [value, startDate]);
  return <EventDateInput {...props} value={value} onChange={onChange} minDate={startDate} />;
};

// End time: only limited by the start time when the event starts and ends on the same day.
const EventEndTimeInput = ({ value, onChange, ...props }) => {
  const form = Form.useFormInstance();
  const startDate = Form.useWatch("startDate", form);
  const endDate = Form.useWatch("endDate", form);
  const startTime = Form.useWatch("startTime", form);
  const minTime = startDate && startDate === endDate && isValidTimeStr(startTime) ? startTime.slice(0, 5) : undefined;
  useEffect(() => {
    if (minTime && isValidTimeStr(value) && value < minTime) onChange?.(minTime);
  }, [value, minTime]);
  return <TimeField {...props} value={value} onChange={onChange} minTime={minTime} />;
};

// Validation for the pair, in case a value arrives from a saved draft or an existing event.
const endDateRule = (t) => ({ getFieldValue }) => ({
  validator(_, value) {
    const start = getFieldValue("startDate");
    return !value || !start || value >= start
      ? Promise.resolve()
      : Promise.reject(new Error(t("eventForm.fields.endDateBeforeStart")));
  },
});
const endTimeRule = (t) => ({ getFieldValue }) => ({
  validator(_, value) {
    const sameDay = getFieldValue("startDate") && getFieldValue("startDate") === getFieldValue("endDate");
    const start = getFieldValue("startTime");
    return !sameDay || !isValidTimeStr(value) || !isValidTimeStr(start) || value.slice(0, 5) >= start.slice(0, 5)
      ? Promise.resolve()
      : Promise.reject(new Error(t("eventForm.fields.endTimeBeforeStart")));
  },
});

// Which language guest-facing WhatsApp messages and wallet cards use. Stored on the event as
// messageLanguage ("arabic" | "english" | "both"); an event with nothing saved is shown as Arabic
// when it has an Arabic title (the server decides the same way). English stays disabled until
// the English WhatsApp templates exist; Both needs an Arabic and an English title.
const MessageLanguageSetting = () => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const saved = Form.useWatch("messageLanguage", form);
  const title = Form.useWatch("title", form);
  const arabicTitle = Form.useWatch("arabicTitle", form);
  const hasBoth = !!(title && title.trim() && arabicTitle && arabicTitle.trim());
  const value = saved || (arabicTitle && arabicTitle.trim() ? "arabic" : "english");
  return (
    <div className="mb-4">
      <div className="text-sm font-medium text-gray-700 mb-2">{t("eventForm.whatsapp.messageLanguageLabel")}</div>
      <Segmented
        block
        value={value}
        onChange={(next) => form.setFieldsValue({ messageLanguage: next })}
        options={[
          { value: "arabic", label: t("eventForm.whatsapp.optionArabic") },
          { value: "english", label: t("eventForm.whatsapp.optionEnglish"), disabled: true },
          { value: "both", label: t("eventForm.whatsapp.optionBoth"), disabled: !hasBoth },
        ]}
      />
      <div className="text-xs text-gray-500 mt-2">
        {t("eventForm.whatsapp.messageLanguageHint")}
        {!hasBoth && <div className="mt-1">{t("eventForm.whatsapp.bothNeedsTitles")}</div>}
      </div>
    </div>
  );
};

const EventFormPage = () => {
  const { t, i18n } = useTranslation();
  const { profile, primaryColor, isVirtual } = useContext(AppContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  // General events: guests get a WhatsApp contact only when this is on (weddings always do).
  const showGuestContact = Form.useWatch("showGuestContact", form);
  const enableSpeakersFeature = Form.useWatch("enableSpeakers", form);
  const enableExhibitorsFeature = Form.useWatch("enableExhibitors", form);
  const enableVipFeature = Form.useWatch("enableVipRegistrations", form);
  // Live values feeding the WhatsApp invitation preview - reused form state, no duplicate store.
  const regularPassColorWatch = Form.useWatch("regularPassColor", form);
  const weddingTitleWatch = Form.useWatch("title", form);
  const latitudeWatch = Form.useWatch("latitude", form);
  const longitudeWatch = Form.useWatch("longitude", form);
  const savedMapPosition =
    latitudeWatch != null && longitudeWatch != null && !isNaN(Number(latitudeWatch)) && !isNaN(Number(longitudeWatch))
      ? { lat: Number(latitudeWatch), lng: Number(longitudeWatch) }
      : undefined;
  const weddingArabicTitleWatch = Form.useWatch("arabicTitle", form);
  const weddingInvitedByWatch = Form.useWatch("invitedBy", form);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [logoPreviewImage, setLogoPreviewImage] = useState("");
  const [passImagePreview, setPassImagePreview] = useState("");
  const [passImageUploading, setPassImageUploading] = useState(false);
  const [isDefaultLogo, setIsDefaultLogo] = useState(false);
  const [gates, setGates] = useState([]);
  // Version of the gate list this form loaded/saved; the server rejects a save built on stale gates
  // (e.g. an organizer added a gate meanwhile) instead of silently deleting them.
  const [gatesVersion, setGatesVersion] = useState(null);

  // Returns true when the error was a gates/event conflict and the user has been told to reload.
  const handleEventConflict = (error) => {
    if (error?.response?.status !== 409) return false;
    Modal.warning({
      title: t("eventForm.gates.conflictTitle"),
      content: error.response?.data?.message || t("eventForm.gates.conflictMessage"),
      okText: t("eventForm.gates.conflictReload"),
      onOk: () => window.location.reload(),
    });
    return true;
  };
  const [organizers, setOrganizers] = useState([]);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [enableArabic, setEnableArabic] = useState(false);
  // Wedding-only: Arabic is always collected for a wedding, this toggles whether
  // the English fields are shown too. Kept separate from enableArabic so the
  // regular (non-wedding) event form's Arabic-optional behavior is untouched.
  const [enableEnglish, setEnableEnglish] = useState(false);
  // UI-only: shows the bride/groom fields. Not stored on the event - an existing event counts as a
  // wedding when it already has bride/groom names (same idea the form already uses to detect one).
  const [isWeddingOccasion, setIsWeddingOccasion] = useState(true);
  // Small toggle in the venue card only: reveals the English City/Country and Venue Name inputs.
  // Independent of the step-0 English content switch; the map still fills them while hidden.
  const [showEnglishLocation, setShowEnglishLocation] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  // The saved event's landing page slug (edit mode), used by the invitation theme preview.
  const [landingSlug, setLandingSlug] = useState("");
  const [pendingOrganizerInvites, setPendingOrganizerInvites] = useState([]);
  const isEditing = isValidGuid(id);
  const isCreateMode = !isEditing;
  const [speakers, setSpeakers] = useState([]);
  const [exhibitors, setExhibitors] = useState([]);
  const [isMarriageEvent, setIsMarriageEvent] = useState(false);
  const [invitationImageUploading, setInvitationImageUploading] = useState(false);
  const [invitationImagePreview, setInvitationImagePreview] = useState("");
  // The entitlement id an existing wedding event was actually created against (from
  // fetchEvent), used to look up the exact backing entitlement rather than guessing.
  const [eventEntitlementId, setEventEntitlementId] = useState(null);
  // The active Wedding entitlement backing this event's real invitation limit. Its
  // maxInvitations replaces the old hardcoded 1000 capacity for marriage events.
  const [weddingEntitlement, setWeddingEntitlement] = useState(null);
  // Tracks the in-flight lookup so submission can be blocked until we know for certain
  // whether a usable Wedding entitlement exists (no silent 1000 fallback allowed).
  const [weddingEntitlementLoading, setWeddingEntitlementLoading] = useState(false);
  // Step layout - used for wedding events only, both when creating (with a final review step
  // and a local draft) and when editing (free navigation between steps, no review, no draft).
  // Non-marriage events keep the original single-page form untouched so their existing
  // behavior (gate auto-save, speakers/exhibitors/sessions, etc.) is unaffected.
  const [currentStep, setCurrentStep] = useState(0);
  // Opened from the guest list checklist ("?section=gates"): jump straight to the entry gates.
  const openGatesSection = searchParams.get("section") === "gates";
  const [gatesOpen, setGatesOpen] = useState(openGatesSection);
  const [whatsAppPreviewOpen, setWhatsAppPreviewOpen] = useState(false);
  const [thankYouPreviewOpen, setThankYouPreviewOpen] = useState(false);
  useEffect(() => {
    if (openGatesSection && isEditing && isMarriageEvent) {
      setCurrentStep(3);
      setGatesOpen(true);
    }
  }, [openGatesSection, isEditing, isMarriageEvent]);
  const [locatingUser, setLocatingUser] = useState(false);
  const draftSaveTimerRef = useRef(null);
  // The last Arabic location we filled in from the map. Lets a later map pick replace it,
  // while a value the user typed or edited themselves is never overwritten.
  const autoArabicLocationRef = useRef("");
  // The invitation sentence last filled in automatically. While the field still holds it (or is empty) it
  // follows the "Invited by" field; once the user edits it, it is theirs and stops following.
  const lastAutoInvitationTextRef = useRef(defaultInvitationText(""));
  // Same idea for Venue Name: the last value taken from a picked venue, so a later pick of a
  // non-venue spot (street, city, country) can clear it without touching what the user typed.
  const autoVenueRef = useRef("");
  const isWizardMode = isMarriageEvent;
  const WIZARD_STEPS = [
    t("eventForm.wizardSteps.coupleDetails"),
    t("eventForm.wizardSteps.dateVenue"),
    t("eventForm.wizardSteps.photosBranding"),
    t("eventForm.wizardSteps.guestsSharing"),
    ...(isCreateMode ? [t("eventForm.wizardSteps.reviewCreate")] : []),
  ];
  const WIZARD_STEP_FIELDS = {
    0: ["title", "arabicTitle", "groomName", "arabicGroomName", "brideName", "arabicBrideName", "shortDescription", "arabicShortDescription", "description", "arabicDescription"],
    1: ["startDate", "endDate", "startTime", "endTime", "latitude", "location", "arabicLocation", "venue", "arabicVenue"],
    2: [],
    3: ["capacity", "registrationOpen"],
  };
  // New weddings and edits of an existing wedding each keep their own browser draft, so a
  // refresh (or an accidental tab close) does not lose unsaved changes.
  const WEDDING_DRAFT_KEY = isCreateMode ? "wedding-draft-new" : `wedding-draft-edit-${id}`;
  // In edit mode, drafts only start saving once the event has loaded from the server.
  const editDraftReadyRef = useRef(false);

  // The invitation preview follows the saved message language. Both shows the Arabic wording with
  // both titles; an event with nothing saved is Arabic when it has an Arabic title.
  const messageLanguageWatch = Form.useWatch("messageLanguage", form);

  // Keep the prefilled invitation sentence in step with "Invited by" until the user edits the sentence.
  useEffect(() => {
    const current = form.getFieldValue("invitationText");
    if (!current || current === lastAutoInvitationTextRef.current) {
      const next = defaultInvitationText(weddingInvitedByWatch);
      lastAutoInvitationTextRef.current = next;
      form.setFieldsValue({ invitationText: next });
    }
  }, [weddingInvitedByWatch, form]);

  // New wedding-wizard events: prefill the Short / Full descriptions with the approved wording. Each box
  // follows "Invited by", the couple's names and the wedding switch until the user edits it; after that
  // it is theirs. Existing events are never touched.
  const groomArWatch = Form.useWatch("arabicGroomName", form);
  const brideArWatch = Form.useWatch("arabicBrideName", form);
  const groomEnWatch = Form.useWatch("groomName", form);
  const brideEnWatch = Form.useWatch("brideName", form);
  const lastAutoDescriptionsRef = useRef({});
  useEffect(() => {
    if (!isCreateMode || !isMarriageEvent) return;
    const fields = [
      { name: "arabicShortDescription", kind: "short", lang: "ar" },
      { name: "arabicDescription", kind: "full", lang: "ar" },
      ...(enableEnglish
        ? [
            { name: "shortDescription", kind: "short", lang: "en" },
            { name: "description", kind: "full", lang: "en" },
          ]
        : []),
    ];
    const updates = {};
    fields.forEach(({ name, kind, lang }) => {
      const current = form.getFieldValue(name);
      if (current && current !== lastAutoDescriptionsRef.current[name]) return; // user's own text
      const next = getDefaultDescription({
        kind,
        lang,
        isWedding: isWeddingOccasion,
        invitedBy: weddingInvitedByWatch,
        groom: lang === "ar" ? groomArWatch : groomEnWatch,
        bride: lang === "ar" ? brideArWatch : brideEnWatch,
      });
      lastAutoDescriptionsRef.current[name] = next;
      updates[name] = next;
    });
    if (Object.keys(updates).length) form.setFieldsValue(updates);
  }, [
    isCreateMode, isMarriageEvent, enableEnglish, isWeddingOccasion, weddingInvitedByWatch,
    groomArWatch, brideArWatch, groomEnWatch, brideEnWatch, form,
  ]);
  const previewLanguage =
    messageLanguageWatch === "english" || (!messageLanguageWatch && !(weddingArabicTitleWatch && weddingArabicTitleWatch.trim()))
      ? "en"
      : "ar";
  const previewArabicTitle =
    messageLanguageWatch === "both" && weddingTitleWatch && weddingArabicTitleWatch
      ? weddingArabicTitleWatch + " / " + weddingTitleWatch
      : weddingArabicTitleWatch;

  // The lines of the WhatsApp message that can be edited either in the inputs or right in the preview.
  // Both write to the same form fields, so they always agree.
  const invitationTextWatch = Form.useWatch("invitationText", form);
  const closingTextWatch = Form.useWatch("closingText", form);
  const guestTitleWatch = Form.useWatch("guestTitle", form);
  const thankYouTextWatch = Form.useWatch("thankYouText", form);
  const setWhatsAppField = (name, value) => {
    form.setFieldsValue({ [name]: value });
    scheduleWeddingDraftSave(); // setFieldsValue does not fire onValuesChange, so save the draft here
  };
  // The preview shows one language; "both" joins the two titles, which can only be edited in the form.
  const previewTitleEditable = messageLanguageWatch !== "both";
  const onPreviewTitleChange = (value) => setWhatsAppField(previewLanguage === "ar" ? "arabicTitle" : "title", value);
  const guestTitleOptions = [
    { value: "السيد/السيدة", label: t("eventForm.whatsapp.guestTitleMrMs") },
    { value: DEFAULT_GUEST_TITLE, label: t("eventForm.whatsapp.guestTitleDear") },
    { value: "السيد", label: t("eventForm.whatsapp.guestTitleMr") },
    { value: "السيدة", label: t("eventForm.whatsapp.guestTitleMrs") },
  ];

  // Date line of the WhatsApp message, worded like the server's EventDateFormatter.Format
  // ("<leading calendar> الموافق <other>" in Arabic, "<leading> (<other>)" in English).
  const weddingStartDateWatch = Form.useWatch("startDate", form);
  const weddingCalendarWatch = Form.useWatch("calendarPreference", form);
  const weddingHijriAdjustWatch = Form.useWatch("hijriAdjustmentDays", form);
  const previewEventDate = (() => {
    const { primary, secondary } = formatDualDate(
      weddingStartDateWatch, previewLanguage, weddingCalendarWatch, weddingHijriAdjustWatch || 0
    );
    if (!primary) return "";
    if (!secondary) return primary;
    return previewLanguage === "ar" ? `${primary} الموافق ${secondary}` : `${primary} (${secondary})`;
  })();

  useEffect(() => {
    if (isEditing) {
      fetchEvent();
    }
    fetchUsers();
  }, [id]);

  // Marriage flow is entered via /event-form?type=marriage (from EventManagementPage's type
  // selector). Only applies on create — editing detects it from the loaded event's own data.
  // Arabic is always collected for a wedding (see enableEnglish), so no Arabic default is
  // needed here.
  useEffect(() => {
    const eventTypeParam = searchParams.get("type");
    if (isCreateMode && eventTypeParam?.toLowerCase() === "marriage") {
      setIsMarriageEvent(true);
      // 9:00 PM is a typical wedding start time in Saudi Arabia; an untouched new wedding
      // starts there instead of the generic 00:01 default (a restored draft overrides it).
      form.setFieldsValue({ eventType: "marriage", startTime: "21:00", messageLanguage: "arabic" });
    }
  }, [searchParams, isCreateMode]);

  // A wedding with no logo gets the platform-wide default wedding logo (managed by the super
  // admin in Settings). Only fills an empty logo, so an uploaded or saved logo is never overwritten;
  // the user can still replace it with their own upload.
  useEffect(() => {
    if (!isMarriageEvent || logoPreviewImage) return;
    let cancelled = false;
    axios
      .get(`${BASE_URL}/Lookup/default-event-logo/marriage`)
      .then((res) => {
        const defaultUrl = res.data?.logoUrl;
        if (cancelled || !defaultUrl) return;
        setLogoPreviewImage(defaultUrl);
        setIsDefaultLogo(true);
        form.setFieldsValue({ logoUrl: defaultUrl });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isMarriageEvent, logoPreviewImage]);

  // Resolve the real invitation limit for a wedding event from the company's purchased
  // entitlement, instead of the old hardcoded 1000 capacity. When editing an existing wedding
  // event, match its own entitlementId; otherwise mirror the backend's own selection rule
  // (oldest active Wedding entitlement first, see EventService.CreateEventAsync).
  // Only virtual companies purchase entitlements - the backend itself only requires/looks up
  // an entitlement when company.IsVirtual (see EventService.CreateEventAsync), so a legacy/
  // non-virtual company must skip this lookup entirely and keep using its existing capacity.
  useEffect(() => {
    if (!isMarriageEvent || !profile?.companyId || !isVirtual) return;
    const fetchWeddingEntitlement = async () => {
      setWeddingEntitlementLoading(true);
      try {
        const { data } = await axios.get(`${BASE_URL}/Company/my-entitlements`);
        const activeWeddingEntitlements = (data || [])
          .filter((e) => e.status === "active" && e.productType === "wedding")
          .sort((a, b) => new Date(a.purchasedAt) - new Date(b.purchasedAt));
        const matched =
          (eventEntitlementId &&
            activeWeddingEntitlements.find((e) => e.id === eventEntitlementId)) ||
          activeWeddingEntitlements[0] ||
          null;
        setWeddingEntitlement(matched);
        // Only trust a real, positive entitlement value here - never fall back to a
        // fabricated default. If unresolved, clear the field so a stale/default value
        // can't be mistaken for the real invitation limit; handleSubmit blocks the save.
        form.setFieldsValue({
          capacity: matched?.maxInvitations > 0 ? matched.maxInvitations : null,
        });
      } catch (error) {
        console.error("Error fetching wedding entitlement:", error);
        setWeddingEntitlement(null);
        form.setFieldsValue({ capacity: null });
      } finally {
        setWeddingEntitlementLoading(false);
      }
    };
    fetchWeddingEntitlement();
  }, [isMarriageEvent, profile?.companyId, eventEntitlementId, isVirtual]);

  // Restore an unsaved wedding draft from this browser, if one exists. Scoped to the create
  // flow only - a real cross-device draft would need a backend "draft event" endpoint, which
  // this is deliberately not touching. Reads the raw "type" param directly rather than
  // isMarriageEvent state, since both are set from the same param on the same mount pass.
  const restoreWeddingDraft = () => {
    try {
      const saved = localStorage.getItem(WEDDING_DRAFT_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      const restored = { ...parsed };
      delete restored._enableArabic;
      delete restored._enableEnglish;
      delete restored._isWeddingOccasion;
      delete restored._showEnglishLocation;
      delete restored._previewImage;
      delete restored._logoPreviewImage;
      delete restored._invitationImagePreview;
      delete restored._passImagePreview;
      delete restored._currentStep;
      delete restored._autoArabicLocation;
      // Back-compat: a draft saved before the switch to native date/time inputs stored an
      // ISO-string "date" pair instead of startDate/endDate - convert it if present.
      if (Array.isArray(parsed.date)) {
        restored.startDate = toDateInputValue(parsed.date[0]);
        restored.endDate = toDateInputValue(parsed.date[1]);
        delete restored.date;
      }
      form.setFieldsValue(restored);
      // A draft saved before these fields existed (or one still holding the default) gets the prefilled defaults.
      const restoredAutoText = defaultInvitationText(restored.invitedBy);
      if (!restored.invitationText || restored.invitationText === restoredAutoText) {
        lastAutoInvitationTextRef.current = restoredAutoText;
        form.setFieldsValue({ invitationText: restoredAutoText });
      }
      if (!restored.closingText) form.setFieldsValue({ closingText: DEFAULT_CLOSING_TEXT });
      if (!restored.guestTitle) form.setFieldsValue({ guestTitle: DEFAULT_GUEST_TITLE });
      if (!restored.thankYouText) form.setFieldsValue({ thankYouText: DEFAULT_THANK_YOU_TEXT });
      if (parsed._autoArabicLocation) autoArabicLocationRef.current = parsed._autoArabicLocation;
      if (parsed._enableArabic) setEnableArabic(true);
      if (parsed._enableEnglish) setEnableEnglish(true);
      if (typeof parsed._isWeddingOccasion === "boolean") setIsWeddingOccasion(parsed._isWeddingOccasion);
      if (parsed._showEnglishLocation) setShowEnglishLocation(true);
      if (parsed._previewImage) setPreviewImage(parsed._previewImage);
      if (parsed._logoPreviewImage) setLogoPreviewImage(parsed._logoPreviewImage);
      if (parsed._invitationImagePreview) setInvitationImagePreview(parsed._invitationImagePreview);
      if (parsed._passImagePreview) setPassImagePreview(parsed._passImagePreview);
      if (typeof parsed._currentStep === "number") setCurrentStep(parsed._currentStep);
      message.info(t("eventForm.messages.draftRestored"));
    } catch (error) {
      console.error("Failed to restore wedding draft:", error);
    }
  };

  useEffect(() => {
    const eventTypeParam = searchParams.get("type");
    if (!isCreateMode || eventTypeParam?.toLowerCase() !== "marriage") return;
    restoreWeddingDraft();
  }, [searchParams, isCreateMode]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${BASE_URL}/User`);

      // Transform users to include both id and name
      const formattedUsers = response.data.map((user) => ({
        id: user.id,
        name: user.fullName || user.name || user.email,
        email: user.email,
      }));

      setUsers(formattedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      // Set empty array if fetch fails
      setUsers([]);
    }
  };

  const sendWalletPassesAfterCreate = async (newEventId) => {
    let sent = 0;
    let failed = 0;
    let skipped = 0;

    for (const speaker of speakers) {
      if (!speaker?.id || !speaker?.email) {
        skipped++;
        continue;
      }

      try {
        await axios.post(`${BASE_URL}/EventWallet/send-card`, {
          type: "speaker",
          eventId: newEventId,
          speakerId: speaker.id,
          email: speaker.email,
        });
        sent++;
      } catch (error) {
        console.error(`Failed to send speaker wallet pass for ${speaker.email}:`, error);
        failed++;
      }
    }

    for (const exhibitor of exhibitors) {
      if (!exhibitor?.id || !exhibitor?.email) {
        skipped++;
        continue;
      }

      try {
        await axios.post(`${BASE_URL}/EventWallet/send-card`, {
          type: "exhibitor",
          eventId: newEventId,
          exhibitorId: exhibitor.id,
          email: exhibitor.email,
        });
        sent++;
      } catch (error) {
        console.error(`Failed to send exhibitor wallet pass for ${exhibitor.email}:`, error);
        failed++;
      }
    }

    return { sent, failed, skipped };
  };

  const fetchEvent = async () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    try {
      //const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BASE_URL}/Event/${id}?companyId=${profile.companyId}`
      );
      const eventData = response.data;

      form.setFieldsValue({
        ...eventData,
        startDate: toDateInputValue(eventData.startDate),
        endDate: toDateInputValue(eventData.endDate),
        startTime: toTimeInputValue(eventData.startTime),
        endTime: toTimeInputValue(eventData.endTime),
        registrationDeadline: toDateInputValue(eventData.registrationDeadline),
        useBackgroundAsHero: true, // Always on (toggle is hidden)
        // Fallback colors for older events that have null in DB
        regularPassColor: eventData.regularPassColor || "#1e3a8a",
        vipPassColor: eventData.vipPassColor || "#b8860b",
        speakerPassColor: eventData.speakerPassColor || "#6b46c1",
        exhibitorPassColor: eventData.exhibitorPassColor || "#10b981",
        
        // Map PascalCase API response to camelCase form fields
        arabicTitle: eventData.arabicTitle || eventData.ArabicTitle,
        arabicShortDescription:
          eventData.arabicShortDescription || eventData.ArabicShortDescription,
        arabicDescription:
          eventData.arabicDescription || eventData.ArabicDescription,
        arabicLocation: eventData.arabicLocation || eventData.ArabicLocation,
        arabicVenue: eventData.arabicVenue || eventData.ArabicVenue,
        groomName: eventData.groomName || eventData.GroomName,
        brideName: eventData.brideName || eventData.BrideName,
        arabicGroomName: eventData.arabicGroomName || eventData.ArabicGroomName,
        arabicBrideName: eventData.arabicBrideName || eventData.ArabicBrideName,
        invitedBy: eventData.invitedBy || eventData.InvitedBy || "",
        // Saved text if the organiser customised it, otherwise the prefilled default (which then follows "Invited by").
        invitationText: eventData.invitationText || eventData.InvitationText || defaultInvitationText(eventData.invitedBy || eventData.InvitedBy),
        closingText: eventData.closingText || eventData.ClosingText || DEFAULT_CLOSING_TEXT,
        guestTitle: eventData.guestTitle || eventData.GuestTitle || DEFAULT_GUEST_TITLE,
        thankYouText: eventData.thankYouText || eventData.ThankYouText || DEFAULT_THANK_YOU_TEXT,
      });
      if (!(eventData.invitationText || eventData.InvitationText)) {
        lastAutoInvitationTextRef.current = defaultInvitationText(eventData.invitedBy || eventData.InvitedBy);
      }

      const eventIsMarriage =
        eventData.eventType === "marriage" ||
        !!(eventData.groomName || eventData.GroomName || eventData.brideName || eventData.BrideName);
      setIsMarriageEvent(eventIsMarriage);
      setIsWeddingOccasion(
        !!(eventData.groomName || eventData.GroomName || eventData.brideName || eventData.BrideName ||
           eventData.arabicGroomName || eventData.ArabicGroomName || eventData.arabicBrideName || eventData.ArabicBrideName)
      );
      setEventEntitlementId(eventData.entitlementId || eventData.EntitlementId || null);
      setInvitationImagePreview(eventData.invitationImageUrl || eventData.InvitationImageUrl || "");
      setLandingSlug(eventData.landingPageSlug || eventData.LandingPageSlug || "");
      setPassImagePreview(eventData.passImageUrl || eventData.PassImageUrl || "");

      // Load background image - check both camelCase and PascalCase
      setPreviewImage(
        eventData.backgroundImageUrl ||
        eventData.BackgroundImageUrl ||
        eventData.bannerImageUrl
      );

      setLogoPreviewImage(eventData.logoUrl || eventData.LogoUrl);


      // Load gates if available
      setGatesVersion(eventData.gatesVersion ?? eventData.GatesVersion ?? null);
      if (eventData.gates) {
        setGates(eventData.gates);
      }

      // Load organizers if available
      if (eventData.organizers) {
        setOrganizers(eventData.organizers);
      }

      // Load sessions if available
      if (eventData.sessions) {
        setSessions(eventData.sessions);
      }

      // Load speakers if available
      if (eventData.speakers) {
        setSpeakers(eventData.speakers);
      }

      // Load exhibitors if available
      if (eventData.exhibitors) {
        setExhibitors(eventData.exhibitors);
      }

      // Enable Arabic toggle if Arabic content exists or if languages includes 'ar'
      if (
        eventData.arabicTitle ||
        eventData.arabicDescription ||
        eventData.languages?.includes("ar")
      ) {
        setEnableArabic(true);
      }

      // Wedding-only: show the English fields on edit if English content already
      // exists, so an organizer who filled them in before doesn't see them vanish.
      if (
        eventIsMarriage &&
        (eventData.title || eventData.groomName || eventData.GroomName ||
          eventData.brideName || eventData.BrideName ||
          eventData.languages?.includes("en"))
      ) {
        setEnableEnglish(true);
      }
      // Layer any unsaved edits from this browser over the freshly loaded event.
      if (eventIsMarriage) restoreWeddingDraft();
      editDraftReadyRef.current = true;

      setLoading(false);
    } catch (error) {
      console.error("Error fetching event:", error);
      message.error(
        t("eventForm.messages.failedLoadEvent", { reason: error.response?.data?.message || error.message })
      );
      setLoading(false);
    }
  };
  // The map search box also accepts free text, so a location only counts once a place or pin
  // has actually been picked (that is what fills latitude/longitude) and the search box is not empty.
  // null = the map has not mounted yet (e.g. another wizard step), so there is nothing to check.
  const mapSearchTextRef = useRef(null);
  const handleMapSearchTextChange = (text) => {
    mapSearchTextRef.current = text;
    // Once the error is showing, re-check as the user types so it clears when they fill the box.
    if (form.getFieldError("latitude").length) form.validateFields(["latitude"]).catch(() => {});
  };
  const locationPinRules = [
    {
      validator: () => {
        const isCoord = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));
        const searchText = mapSearchTextRef.current;
        const hasText = searchText === null || searchText.trim() !== "";
        return hasText && isCoord(form.getFieldValue("latitude")) && isCoord(form.getFieldValue("longitude"))
          ? Promise.resolve()
          : Promise.reject(new Error(t("eventForm.fields.locationPinRequired")));
      },
    },
  ];

  const handleLocationSelect = (locationData) => {
    const englishLocation = locationData.city && locationData.country
      ? `${locationData.city}, ${locationData.country}`
      : locationData.city || locationData.country || "";
    // Only a real venue supplies a Venue Name; for a street, city or country the "name" is just
    // the place label (e.g. "Saudi Arabia"), which would wrongly satisfy the required field.
    const isVenue = (locationData.types || []).some((type) => type === "establishment" || type === "point_of_interest");
    const currentVenue = form.getFieldValue("venue") || "";
    const venueIsAuto = !currentVenue || currentVenue === autoVenueRef.current;
    let nextVenue = currentVenue;
    if (isVenue && locationData.name) {
      nextVenue = locationData.name;
    } else if (venueIsAuto) {
      nextVenue = "";
    }
    autoVenueRef.current = isVenue && locationData.name ? locationData.name : "";
    form.setFieldsValue({
      location: englishLocation,
      venue: nextVenue,
      address: locationData.address || "",
      latitude: locationData.location?.lat || null,
      longitude: locationData.location?.lng || null,
    });
    // Clear a stale "pick a location" error now that a place has been chosen.
    form.validateFields(["latitude"]).catch(() => {});
    // setFieldsValue does not fire onValuesChange, so persist the picked location to the
    // draft explicitly - otherwise a refresh loses everything the map filled in.
    scheduleWeddingDraftSave();

    // Auto-fill the Arabic City/Country from Google's own Arabic place data, so
    // wedding organizers using the Arabic-primary flow don't have to type it manually.
    // Only fills it in if the user hasn't already typed something there themselves -
    // never overwrites a manual edit. Venue Name has no equivalent auto-fill here since
    // Google's Place Details (which has the venue's real Arabic name) can't be called
    // directly from the browser - see the earlier discussion on why that needs a backend
    // proxy or a separate machine-translation step, neither of which is wired up yet.
    const canAutoFillArabicLocation = () => {
      const current = form.getFieldValue("arabicLocation");
      return !current || current === autoArabicLocationRef.current;
    };
    // Always refresh an auto-filled value (falling back to the English one) so a new pick can't
    // leave the Arabic field pointing at the previous place.
    const applyArabicLocation = (value) => {
      if (!value || !canAutoFillArabicLocation()) return;
      autoArabicLocationRef.current = value;
      form.setFieldsValue({ arabicLocation: value });
      scheduleWeddingDraftSave();
    };
    if ((isMarriageEvent || enableArabic) && canAutoFillArabicLocation() && locationData.location?.lat && locationData.location?.lng) {
      axios
        .get(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${locationData.location.lat},${locationData.location.lng}&language=ar&key=AIzaSyB9dtJwoDszhkMZ2CeD8hR-S-C06CmrfEE`,
          {headers: {Authorization: undefined}}
        )
        .then((response) => {
          const result = response.data?.results?.[0];
          const components = result?.address_components || [];
          const getComponent = (type) => components.find((c) => c.types.includes(type))?.long_name;
          const arabicCity = getComponent("locality") || getComponent("administrative_area_level_2") || "";
          const arabicCountry = getComponent("country") || "";
          const arabicCombined = arabicCity && arabicCountry
            ? `${arabicCity}, ${arabicCountry}`
            : arabicCity || arabicCountry || "";
          applyArabicLocation(arabicCombined || englishLocation);
        })
        .catch((error) => {
          console.error("Arabic reverse geocoding failed:", error);
          applyArabicLocation(englishLocation);
        });
    }
  };

  const saveWeddingDraft = () => {
    if (!isWizardMode || (!isCreateMode && !editDraftReadyRef.current)) return;
    try {
      const values = form.getFieldsValue(true);
      const serializable = {
        ...values,
        _enableArabic: enableArabic,
        _enableEnglish: enableEnglish,
        _isWeddingOccasion: isWeddingOccasion,
        _showEnglishLocation: showEnglishLocation,
        _previewImage: previewImage,
        _logoPreviewImage: logoPreviewImage,
        _invitationImagePreview: invitationImagePreview,
        _passImagePreview: passImagePreview,
        _currentStep: currentStep,
        _autoArabicLocation: autoArabicLocationRef.current,
      };
      localStorage.setItem(WEDDING_DRAFT_KEY, JSON.stringify(serializable));
    } catch (error) {
      console.error("Failed to save wedding draft:", error);
    }
  };

  // Submit validates every field, including ones on steps that aren't currently visible, so
  // jump to the first step that has an error instead of failing silently.
  const focusFirstInvalidField = (errorFields, delay = 0) => {
    const name = errorFields?.[0]?.name;
    if (!name) return;
    // Wait a tick so a step switch has made the field visible before scrolling to it.
    setTimeout(() => {
      form.scrollToField(name, { behavior: "smooth", block: "center" });
      form.getFieldInstance?.(name)?.focus?.();
    }, delay);
  };

  const handleFinishFailed = ({ errorFields }) => {
    if (!errorFields?.length) return;
    if (!isWizardMode) {
      focusFirstInvalidField(errorFields);
      return;
    }
    const failed = new Set(errorFields.map((f) => f.name[0]));
    const step = Object.entries(WIZARD_STEP_FIELDS).find(([, fields]) => fields.some((n) => failed.has(n)));
    const stepFields = step ? WIZARD_STEP_FIELDS[step[0]] : null;
    const ordered = stepFields ? errorFields.filter((f) => stepFields.includes(f.name[0])) : errorFields;
    if (step) setCurrentStep(Number(step[0]));
    focusFirstInvalidField(ordered, step && Number(step[0]) !== currentStep ? 300 : 0);
  };

  // Every step change should land at the top of the new step, not wherever the user was
  // scrolled on the previous one.
  const stepsTopRef = useRef(null);
  const firstStepRenderRef = useRef(true);
  useEffect(() => {
    if (firstStepRenderRef.current) {
      firstStepRenderRef.current = false;
      return;
    }
    stepsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentStep]);

  // Cancel means discard: drop the edit draft so it does not resurface next time. The new-wedding
  // draft is kept on purpose so organizers can come back to it.
  const handleCancel = () => {
    if (!isCreateMode) {
      try {
        localStorage.removeItem(WEDDING_DRAFT_KEY);
      } catch (error) {
        console.error("Failed to clear wedding draft:", error);
      }
    }
    navigate("/events");
  };

  const scheduleWeddingDraftSave = () => {
    if (!isWizardMode || (!isCreateMode && !editDraftReadyRef.current)) return;
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(saveWeddingDraft, 800);
  };

  const goToNextStep = async () => {
    try {
      await form.validateFields(WIZARD_STEP_FIELDS[currentStep] || []);
      setCurrentStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
      setTimeout(saveWeddingDraft, 0);
    } catch (error) {
      // Ant Design shows the inline messages; bring the first one into view.
      focusFirstInvalidField(error?.errorFields);
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const goToStep = async (targetStep) => {
    if (targetStep === currentStep) return;
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
      setTimeout(saveWeddingDraft, 0);
      return;
    }
    try {
      await form.validateFields(WIZARD_STEP_FIELDS[currentStep] || []);
      setCurrentStep(targetStep);
      setTimeout(saveWeddingDraft, 0);
    } catch (error) {
      focusFirstInvalidField(error?.errorFields);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      message.error(t("eventForm.messages.locationUnavailable"));
      return;
    }
    setLocatingUser(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await axios.get(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyB9dtJwoDszhkMZ2CeD8hR-S-C06CmrfEE`,
            { headers: { Authorization: undefined } }
          );
          const result = response.data?.results?.[0];
          const components = result?.address_components || [];
          const getComponent = (type) => components.find((c) => c.types.includes(type))?.long_name;
          const city = getComponent("locality") || getComponent("administrative_area_level_2") || "";
          const country = getComponent("country") || "";
          handleLocationSelect({
            city,
            country,
            name: form.getFieldValue("venue") || "",
            address: result?.formatted_address || "",
            location: { lat: latitude, lng: longitude },
          });
          message.success(t("eventForm.messages.locationDetected"));
        } catch (error) {
          console.error("Reverse geocoding failed:", error);
          message.error(t("eventForm.messages.reverseGeocodeFailed"));
          form.setFieldsValue({ latitude, longitude });
        } finally {
          setLocatingUser(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        message.error(t("eventForm.messages.geolocationError"));
        setLocatingUser(false);
      }
    );
  };

  const handleGatesChange = async (updatedGates) => {
    setGates(updatedGates);

    if (isEditing && id) {
      try {
        const values = form.getFieldsValue();

        const simplifiedOrganizers = organizers.map((org) => ({
          userId: org.userId,
          userName: org.userName,
          userEmail: org.userEmail,
          fullName: org.fullName,
          isActive: org.isActive,
          addedAt: org.addedAt,
          notes: org.notes,
        }));

        const eventData = {
          ...values,
          // English fields are hidden unless the English toggle is on: keep the map-filled value,
          // otherwise fall back to the Arabic one so the API's required fields stay populated.
          venue: values.venue || form.getFieldValue("venue") || values.arabicVenue || "",
          location: values.location || form.getFieldValue("location") || values.arabicLocation || "",
          companyId: profile.companyId,
          createdBy: profile.name,
          languages: isMarriageEvent
            ? (enableEnglish ? ["ar", "en"] : ["ar"])
            : (enableArabic ? ["en", "ar"] : ["en"]),
          useBackgroundAsHero: true,
          UseBackgroundAsHero: true,

          startDate: values.startDate || null,
          endDate: values.endDate || null,
          startTime: isValidTimeStr(values.startTime) ? `${values.startTime}:00` : null,

          registrationDeadline: values.registrationDeadline || null,
          BackgroundImageUrl: previewImage,
          LogoUrl: logoPreviewImage,
          status: isMarriageEvent || values.registrationOpen ? "active" : "draft",

          gates: updatedGates,
          gatesVersion: gatesVersion,

          organizers: simplifiedOrganizers,
          sessions: sessions,
          requireGateValidation: updatedGates && updatedGates.length > 0,

          ArabicTitle: (isMarriageEvent || enableArabic) ? values.arabicTitle : null,
          ArabicShortDescription: (isMarriageEvent || enableArabic) ? values.arabicShortDescription : null,
          ArabicDescription: (isMarriageEvent || enableArabic) ? values.arabicDescription : null,
          ArabicLocation: (isMarriageEvent || enableArabic) ? (values.arabicLocation || values.location || form.getFieldValue("location") || null) : null,
          ArabicVenue: (isMarriageEvent || enableArabic) ? (values.arabicVenue || values.venue || form.getFieldValue("venue") || null) : null,
        };

        if (isValidTimeStr(values.endTime)) {
          eventData.endTime = `${values.endTime}:00`;
        } else {
          delete eventData.endTime;
        }

        // 3. SEND REQUEST
        const autosaveResponse = await axios.put(
          `${BASE_URL}/Event/${id}?companyId=${profile.companyId}`,
          eventData
        );
        setGatesVersion(autosaveResponse.data?.gatesVersion ?? null);

      } catch (error) {
        if (handleEventConflict(error)) return;
        console.error('Failed to auto-save gates:', error);
      }
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Wedding invitation limit must come strictly from an active Wedding entitlement -
      // block creation rather than silently falling back to a fabricated capacity if no
      // usable entitlement is resolved (covers both "no active entitlement" and
      // "entitlement exists but maxInvitations is missing/invalid"). This requirement only
      // applies to virtual companies - a legacy/non-virtual company (e.g. one with
      // HasEventAccess/MaxEventsCount set by SuperAdmin) never purchases entitlements and
      // must be able to create/edit weddings exactly as it always could.
      if (isMarriageEvent && isVirtual) {
        if (weddingEntitlementLoading) {
          message.error(t("eventForm.messages.verifyingWeddingPackage"));
          setLoading(false);
          return;
        }
        if (!(weddingEntitlement?.maxInvitations > 0)) {
          message.error(
            t("eventForm.messages.noWeddingPackage")
          );
          setLoading(false);
          return;
        }
      }

      // Validate required date/time fields
      if (!values.startDate || !values.endDate) {
        message.error(t("eventForm.messages.selectStartEndDates"));
        setLoading(false);
        return;
      }

      if (!isValidTimeStr(values.startTime)) {
        message.error(t("eventForm.messages.validStartTime"));
        setLoading(false);
        return;
      }

      // Simplify organizers - remove role and permissions for now
      const simplifiedOrganizers = organizers.map((org) => ({
        userId: org.userId,
        userName: org.userName,
        userEmail: org.userEmail,
        fullName: org.fullName,
        isActive: org.isActive,
        addedAt: org.addedAt,
        notes: org.notes,
      }));

      console.log(
        "Form values.useBackgroundAsHero:",
        values.useBackgroundAsHero
      );

      const eventData = {
        ...values,
        // English fields are hidden unless the English toggle is on: keep the map-filled value,
        // otherwise fall back to the Arabic one so the API's required fields stay populated.
        venue: values.venue || form.getFieldValue("venue") || values.arabicVenue || "",
        location: values.location || form.getFieldValue("location") || values.arabicLocation || "",
        companyId: profile.companyId,
        createdBy: profile.name,
        languages: isMarriageEvent
          ? (enableEnglish ? ["ar", "en"] : ["ar"])
          : (enableArabic ? ["en", "ar"] : ["en"]), // Set languages based on Arabic/English toggle
        useBackgroundAsHero: true, // Always on: the background image is always the hero banner (camelCase)
        UseBackgroundAsHero: true, // PascalCase for API compatibility
        startDate: values.startDate,
        endDate: values.endDate,
        // Map time fields to HH:mm:ss for the API
        startTime: `${values.startTime}:00`,

        registrationDeadline: values.registrationDeadline || null,
        BackgroundImageUrl: previewImage, // PascalCase for API
        LogoUrl: logoPreviewImage,
        status: isMarriageEvent ? "active" : (values.registrationOpen ? "active" : "draft"),
        eventType: isMarriageEvent ? "marriage" : values.eventType,
        // Wedding capacity: for a virtual company this reflects the purchased entitlement's
        // real invitation limit - guaranteed positive here since handleSubmit already
        // blocked submission above if no valid entitlement was resolved. Registration
        // enforcement itself also reads maxInvitations directly from the entitlement
        // regardless of this value (unchanged, backend-side, see
        // EventRegistrationService.RegisterForEventAsync). For a legacy/non-virtual
        // company there is no entitlement to read from, so preserve the pre-entitlement
        // capacity behavior (the event's existing stored capacity, defaulting to 1000 for
        // a brand-new wedding, same as the last committed behavior).
        capacity: isMarriageEvent
          ? (isVirtual ? weddingEntitlement.maxInvitations : (values.capacity || 1000))
          : values.capacity,
        gates: gates, // Add gates to event data
        gatesVersion: gatesVersion,
        organizers: simplifiedOrganizers, // Add simplified organizers
        sessions: sessions, // Add sessions to event data
        speakers: speakers,
        exhibitors: exhibitors,
        requireGateValidation: gates && gates.length > 0, // Auto-enable if gates exist
        // Map camelCase form fields to PascalCase for API (C# convention)
        ArabicTitle: (isMarriageEvent || enableArabic) ? values.arabicTitle : null,
        ArabicShortDescription: (isMarriageEvent || enableArabic)
          ? values.arabicShortDescription
          : null,
        ArabicDescription: (isMarriageEvent || enableArabic) ? values.arabicDescription : null,
        ArabicLocation: (isMarriageEvent || enableArabic) ? (values.arabicLocation || values.location || form.getFieldValue("location") || null) : null,
        ArabicVenue: (isMarriageEvent || enableArabic) ? (values.arabicVenue || values.venue || form.getFieldValue("venue") || null) : null,
        // Marriage-specific fields (inert/ignored server-side when eventType isn't "marriage")
        InvitationImageUrl: invitationImagePreview,
        PassImageUrl: passImagePreview || null,
        GroomName: values.groomName,
        BrideName: values.brideName,
        ArabicGroomName: values.arabicGroomName,
        ArabicBrideName: values.arabicBrideName,
        InvitedBy: values.invitedBy,
        // A value that is still the default is sent as empty, so the server keeps using its own default
        // (and the sentence keeps following "Invited by").
        InvitationText: values.invitationText === defaultInvitationText(values.invitedBy) ? undefined : values.invitationText,
        ClosingText: values.closingText === DEFAULT_CLOSING_TEXT ? undefined : values.closingText,
        GuestTitle: values.guestTitle === DEFAULT_GUEST_TITLE ? undefined : values.guestTitle,
        ThankYouText: values.thankYouText === DEFAULT_THANK_YOU_TEXT ? undefined : values.thankYouText,
      };

      // Only add endTime if it exists and is valid
      if (isValidTimeStr(values.endTime)) {
        eventData.endTime = `${values.endTime}:00`;
      } else {
        // Remove endTime from payload if not valid
        delete eventData.endTime;
      }

      console.log("Event data being sent to API:", {
        useBackgroundAsHero: eventData.useBackgroundAsHero,
        UseBackgroundAsHero: eventData.UseBackgroundAsHero,
      });

      if (isEditing) {
        const saveResponse = await axios.put(
          `${BASE_URL}/Event/${id}?companyId=${profile.companyId}`,
          eventData
        );
        setGatesVersion(saveResponse.data?.gatesVersion ?? null);
        message.success(t("eventForm.messages.eventUpdated"));
        try {
          localStorage.removeItem(WEDDING_DRAFT_KEY);
        } catch (error) {
          console.error("Failed to clear wedding draft:", error);
        }
      } else {
        const response = await axios.post(`${BASE_URL}/Event`, eventData);
        const newEventId = response.data?.id || response.data?.eventId;

        // Send pending organizer invites
        if (isValidGuid(newEventId) && pendingOrganizerInvites.length > 0) {
          message.info(t("eventForm.messages.sendingInvites", { count: pendingOrganizerInvites.length }));

          let successCount = 0;
          let failCount = 0;

          for (const invite of pendingOrganizerInvites) {
            try {
              await axios.post(
                `${BASE_URL}/Organizer/event/${newEventId}/invite`,
                {
                  Email: invite.userEmail,
                  FullName: invite.fullName,
                  Notes: invite.notes,
                  PhoneNumber: invite.phoneNumber || null,
                  IsGuestContact: !!invite.isGuestContact
                }
              );
              successCount++;
            } catch (error) {
              console.error(`Failed to invite ${invite.email}:`, error);
              failCount++;
            }
          }

          if (successCount > 0) {
            message.success(t("eventForm.messages.invitesEventCreatedSent", { count: successCount }));
          }
          if (failCount > 0) {
            message.warning(t("eventForm.messages.invitesFailed", { count: failCount }));
          }

          // Clear pending invites
          setPendingOrganizerInvites([]);
        } else if (!isValidGuid(newEventId) && pendingOrganizerInvites.length > 0) {
          message.warning(t("eventForm.messages.invitesSkippedNoEventId"));
        } else {
          message.success(t("eventForm.messages.eventCreated"));
        }

        if (isValidGuid(newEventId)) {
          const walletResult = await sendWalletPassesAfterCreate(newEventId);

          if (walletResult.sent > 0 && walletResult.failed === 0) {
            message.success(t("eventForm.messages.walletSentSuccess", { sent: walletResult.sent, skipped: walletResult.skipped }));
          } else if (walletResult.sent > 0 || walletResult.failed > 0 || walletResult.skipped > 0) {
            message.warning(t("eventForm.messages.walletSummary", { sent: walletResult.sent, failed: walletResult.failed, skipped: walletResult.skipped }));
          }
        } else {
          message.warning(t("eventForm.messages.walletSkippedNoEventId"));
        }

        try {
          localStorage.removeItem(WEDDING_DRAFT_KEY);
        } catch (error) {
          console.error("Failed to clear wedding draft:", error);
        }

        navigate(`/events`);
      }

      // Don't navigate away - stay on the same page
      // navigate('/events');
    } catch (error) {
      console.error("Error saving event:", error);
      if (handleEventConflict(error)) return;
      message.error(
        t("eventForm.messages.failedSaveEvent", { reason: error.response?.data?.message || error.message })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (info) => {
    // 1. Get the file object
    const file = info.file.originFileObj || info.file;
    // 2. Validate it's an image
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error(t("eventForm.messages.invalidImageType"));
      return;
    }
    // 3. Create Form Data for Upload
    const formData = new FormData();
    formData.append('file', file);
    try {
      message.loading({ content: t("eventForm.messages.uploadingImage"), key: 'uploading' });

      // 4. Send to our new API Endpoint
      const response = await axios.post(`${BASE_URL}/Upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      // 5. Success! Get the URL
      const imageUrl = response.data.url;

      message.success({ content: t("eventForm.messages.imageUploaded"), key: 'uploading' });
      // 6. Update Form State with the URL (NOT Base64)
      setPreviewImage(imageUrl);
      form.setFieldsValue({ backgroundImageUrl: imageUrl });
    } catch (error) {
      console.error("Upload error:", error);
      message.error({ content: t("eventForm.messages.uploadFailedGeneric"), key: 'uploading' });
    }
  };

  const handleLogoUpload = async (info) => {
    const file = info.file.originFileObj || info.file;
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error(t("eventForm.messages.invalidImageType"));
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      setLogoUploading(true);
      message.loading({ content: t("eventForm.messages.uploadingLogo"), key: 'logoUploading' });

      const response = await axios.post(`${BASE_URL}/Upload/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = response.data.url;

      message.success({ content: t("eventForm.messages.logoUploaded"), key: 'logoUploading' });
      setLogoPreviewImage(imageUrl);
      setIsDefaultLogo(false);
      form.setFieldsValue({ logoUrl: imageUrl });
    } catch (error) {
      console.error("Upload error:", error);
      message.error({ content: t("eventForm.messages.logoUploadFailed"), key: 'logoUploading' });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleInvitationImageUpload = async (info) => {
    const file = info.file.originFileObj || info.file;
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error(t("eventForm.messages.invalidImageType"));
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      setInvitationImageUploading(true);
      message.loading({ content: t("eventForm.messages.uploadingInvitationImage"), key: 'invitationImageUploading' });

      const response = await axios.post(`${BASE_URL}/Upload/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = response.data.url;

      message.success({ content: t("eventForm.messages.invitationImageUploaded"), key: 'invitationImageUploading' });
      setInvitationImagePreview(imageUrl);
      form.setFieldsValue({ invitationImageUrl: imageUrl });
    } catch (error) {
      console.error("Upload error:", error);
      message.error({ content: t("eventForm.messages.invitationImageUploadFailed"), key: 'invitationImageUploading' });
    } finally {
      setInvitationImageUploading(false);
    }
  };

  // Uploads the already-cropped digital card image and resolves to its stored URL.
  const uploadPassImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      setPassImageUploading(true);
      const response = await axios.post(`${BASE_URL}/Upload/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data.url;
    } finally {
      setPassImageUploading(false);
    }
  };

  const passPreviewData = () => {
    const values = form.getFieldsValue();
    return {
      title: values.title,
      dateText: values.startDate ? formatDate(values.startDate) : "",
      venue: values.venue || values.location,
      logoUrl: logoPreviewImage,
      color: regularPassColorWatch || values.regularPassColor || "#1e3a8a",
    };
  };

  const previewLanding = () => {
    // For existing events, use the saved slug; for new events, generate from title
    let slug;

    const title = form.getFieldValue("title");
    if (!title) {
      message.warning(t("eventForm.messages.enterEventTitleFirst"));
      return;
    }

    // Generate slug from title for subdomain
    const titleSlug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
      
    const landingUrl = `${LANDING_PAGE_URL}/events/${titleSlug}`;
    console.log("Preview URL:", landingUrl); // Debug log
    console.log("LANDING_PAGE_URL:", LANDING_PAGE_URL); // Debug log
    setPreviewUrl(landingUrl);
    setPreviewModalVisible(true);
  };

  return (
    <>
      <style jsx>{`
        .responsive-table-container {
          width: 100%;
          max-width: 100%;
        }

        .responsive-table-container .ant-table {
          font-size: 12px;
        }

        .responsive-table-container .ant-table-thead > tr > th {
          padding: 8px 4px !important;
          font-size: 11px !important;
          white-space: nowrap;
        }

        .responsive-table-container .ant-table-tbody > tr > td {
          padding: 8px 4px !important;
          font-size: 11px !important;
          word-break: break-word;
        }

        .responsive-table-container .ant-btn {
          font-size: 11px !important;
          padding: 2px 6px !important;
          height: auto !important;
          min-height: 24px;
        }

        .responsive-table-container .ant-card-head {
          padding: 8px 12px !important;
          min-height: auto !important;
        }

        .responsive-table-container .ant-card-head-title {
          font-size: 14px !important;
        }

        .responsive-table-container .ant-card-body {
          padding: 12px !important;
        }

        @media (max-width: 768px) {
          .responsive-table-container .ant-table-thead > tr > th,
          .responsive-table-container .ant-table-tbody > tr > td {
            padding: 4px 2px !important;
            font-size: 10px !important;
          }

          .responsive-table-container .ant-btn {
            font-size: 10px !important;
            padding: 1px 4px !important;
          }
        }

        /* Native date/time inputs, styled to match antd's "large" text inputs. Using the
           browser's own date/time picker (instead of antd's RangePicker/TimePicker panel)
           avoids the long scrollable list UX on mobile - iOS shows spinning wheels and
           Android shows a compact calendar / round clock face, both native to the OS. */
        .native-date-input,
        .native-time-input {
          width: 100%;
          height: 40px;
          padding: 4px 11px;
          font-size: 14px;
          line-height: 1.5715;
          color: rgba(0, 0, 0, 0.88);
          background-color: #fff;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .time-input-wrap {
          position: relative;
        }

        .time-input-display {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .time-input-overlay {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }

        .time-input-wrap:focus-within .time-input-display {
          border-color: #4096ff;
          box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.1);
        }

        .native-date-input:hover,
        .native-time-input:hover {
          border-color: #4096ff;
        }

        .native-date-input:focus,
        .native-time-input:focus {
          border-color: #4096ff;
          box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.1);
          outline: none;
        }

        /* Mobile touch-target and readability improvements for the event/wedding form.
           Kept scoped to inputs/buttons here rather than global overrides so nothing
           outside this page is affected. 16px min font on inputs avoids iOS auto-zoom
           on focus. */
        @media (max-width: 576px) {
          .ant-form-item .ant-input,
          .ant-form-item .ant-input-number,
          .ant-form-item .ant-input-number-input,
          .ant-form-item .ant-select-selector,
          .ant-form-item .ant-picker,
          .ant-form-item textarea.ant-input,
          .ant-form-item .native-date-input,
          .ant-form-item .native-time-input {
            font-size: 16px !important;
            min-height: 44px;
          }

          .ant-form-item .ant-select-selector {
            display: flex;
            align-items: center;
          }

          .form-actions-card .ant-btn {
            min-height: 48px;
            font-size: 16px;
          }

          .ant-upload-wrapper .ant-btn {
            min-height: 44px;
          }

          .ant-collapse-header {
            min-height: 44px;
            align-items: center !important;
          }

          /* Keep the wizard step indicator as compact numbered circles on
             narrow screens instead of wrapping/stacking full step titles. */
          .wizard-steps-bar .ant-steps-item-title {
            font-size: 11px;
            line-height: 1.2;
          }

          .wizard-steps-bar .ant-steps-item-description {
            display: none;
          }
        }
      `}</style>
      <Layout className="min-h-full bg-transparent">
        <header className="py-4 bg-transparent">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/events")}
                aria-label={t("eventForm.backToEvents")}
              >
                <span className="hidden sm:inline">{t("eventForm.backToEvents")}</span>
              </Button>
              <div style={{ minWidth: 0 }}>
                <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-white break-words">
                  {isEditing ? t("eventForm.editEventTitle") : t("eventForm.createNewEventTitle")}
                </h1>
              </div>
            </div>
            <div className="flex space-x-2">
              {/* <Button icon={<EyeOutlined />} onClick={previewLanding}>
                Preview Landing
              </Button> */}
            </div>
          </div>
        </header>
        <Content className="rounded-lg px-5 py-6 shadow bg-white sm:px-6">
          <div className="bg-white">
            <p className="text-gray-600 mb-6">
              {isEditing
                ? t("eventForm.updateEventSubtitle")
                : t("eventForm.createEventSubtitle")}
            </p>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              onFinishFailed={handleFinishFailed}
              onValuesChange={scheduleWeddingDraftSave}
              initialValues={{
                eventType: "conference",
                category: "business",
                capacity: 100,
                registrationOpen: true,
                useBackgroundAsHero: true, // Always on (toggle is hidden)
                startTime: "00:01",
                endTime: "23:59",
                regularPassColor: "#1e3a8a",
                vipPassColor: "#b8860b",
                speakerPassColor: "#6b46c1",
                exhibitorPassColor: "#10b981",
                invitationText: defaultInvitationText(""),
                closingText: DEFAULT_CLOSING_TEXT,
                guestTitle: DEFAULT_GUEST_TITLE,
                thankYouText: DEFAULT_THANK_YOU_TEXT,
              }}
            >
              <Form.Item name="messageLanguage" hidden><Input /></Form.Item>
              <Form.Item name="calendarPreference" hidden><Input /></Form.Item>
              <Form.Item name="hijriAdjustmentDays" hidden><Input /></Form.Item>

              {isWizardMode && (
                <div className="mb-6 wizard-steps-bar" ref={stepsTopRef} style={{ scrollMarginTop: 72 }}>
                  <Steps
                    current={currentStep}
                    items={WIZARD_STEPS.map((title) => ({ title }))}
                    responsive={false}
                    size="small"
                    onChange={goToStep}
                  />
                </div>
              )}

              {!isWizardMode && (
              <Row gutter={24}>
                <Col xs={24} lg={16}>
                  <Card title={isMarriageEvent ? t("eventForm.sections.weddingDetails") : t("eventForm.sections.basicInformation")} className="mb-6">
                    {isMarriageEvent ? (
                      <>
                        {/* English toggle lives near the top for Wedding so every field pair
                            below can react to it immediately. Arabic is always collected. */}
                        <Card
                          size="small"
                          style={{ marginBottom: 8, backgroundColor: "#f0f5ff" }}
                          styles={{ body: { padding: "8px 12px" } }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Space>
                              <span style={{ fontSize: 16 }}>🌐</span>
                              <div>
                                <strong>{t("eventForm.sections.englishContent")}</strong>
                                <div style={{ fontSize: 11, color: "#666" }}>
                                  {t("eventForm.sections.englishContentHint")}
                                </div>
                              </div>
                            </Space>
                            <Switch size="small"
                              checked={enableEnglish}
                              onChange={(checked) => {
                                setEnableEnglish(checked);
                                if (!checked) {
                                  form.setFieldsValue({
                                    title: undefined,
                                    groomName: undefined,
                                    brideName: undefined,
                                    shortDescription: undefined,
                                    description: undefined,
                                  });
                                }
                              }}
                            />
                          </div>
                        </Card>

                        <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: "8px 12px" } }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                            <div>
                              <strong>{t("eventForm.fields.weddingOccasionLabel")}</strong>
                              <div style={{ fontSize: 11, color: "#666" }}>
                                {t("eventForm.fields.weddingOccasionHint")}
                              </div>
                            </div>
                            <Switch size="small"
                              checked={isWeddingOccasion}
                              onChange={(checked) => {
                                setIsWeddingOccasion(checked);
                                if (!checked) {
                                  form.setFieldsValue({
                                    groomName: undefined,
                                    brideName: undefined,
                                    arabicGroomName: undefined,
                                    arabicBrideName: undefined,
                                  });
                                }
                              }}
                            />
                          </div>
                        </Card>

                        <Row gutter={16}>
                          <Col xs={24} md={enableEnglish ? 12 : 24}>
                            <Form.Item
                              name="arabicTitle"
                              label={t("eventForm.fields.arabicTitleLabel")}
                              rules={[{ required: true, message: t("eventForm.fields.arabicTitleRequired") }]}
                            >
                              <Input size="large" dir="rtl" placeholder={t("eventForm.fields.arabicTitlePlaceholderWedding")} style={{ direction: "rtl" }} />
                            </Form.Item>
                          </Col>
                          {enableEnglish && (
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="title"
                                label={t("eventForm.fields.weddingTitleLabel")}
                                rules={[{ required: false }]}
                              >
                                <Input size="large" placeholder={t("eventForm.fields.weddingTitlePlaceholder")} />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>

                        {isWeddingOccasion && (
                          <>
                        <Row gutter={16}>
                          <Col xs={24} md={enableEnglish ? 12 : 24}>
                            <Form.Item name="arabicGroomName" label={t("eventForm.fields.arabicGroomNameLabel")} rules={[{ required: true, message: t("eventForm.fields.arabicGroomNameRequired") }]}>
                              <Input dir="rtl" placeholder={t("eventForm.fields.arabicGroomNamePlaceholder")} style={{ direction: "rtl" }} />
                            </Form.Item>
                          </Col>
                          {enableEnglish && (
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="groomName"
                                label={t("eventForm.fields.groomNameLabel")}
                                rules={[{ required: false }]}
                              >
                                <Input placeholder={t("eventForm.fields.groomNamePlaceholder")} />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>

                        <Row gutter={16}>
                          <Col xs={24} md={enableEnglish ? 12 : 24}>
                            <Form.Item name="arabicBrideName" label={t("eventForm.fields.arabicBrideNameLabel")} rules={[{ required: true, message: t("eventForm.fields.arabicBrideNameRequired") }]}>
                              <Input dir="rtl" placeholder={t("eventForm.fields.arabicBrideNamePlaceholder")} style={{ direction: "rtl" }} />
                            </Form.Item>
                          </Col>
                          {enableEnglish && (
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="brideName"
                                label={t("eventForm.fields.brideNameLabel")}
                                rules={[{ required: false }]}
                              >
                                <Input placeholder={t("eventForm.fields.brideNamePlaceholder")} />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>
                          </>
                        )}

                        {/* No ArabicInvitedBy field exists on EventDto/Event - kept single-column
                            and English-only rather than inventing a new backend field. */}
                        <Form.Item name="invitedBy" label={t("eventForm.fields.invitedByLabel")}>
                          <Input placeholder={t("eventForm.fields.invitedByPlaceholder")} />
                        </Form.Item>

                        <Row gutter={16}>
                          <Col xs={24} md={enableEnglish ? 12 : 24}>
                            <Form.Item name="arabicShortDescription" label={t("eventForm.fields.arabicShortDescriptionLabel")} rules={[{ required: true, message: t("eventForm.fields.arabicShortDescriptionRequired") }]}>
                              <Input dir="rtl" placeholder={t("eventForm.fields.arabicShortDescriptionPlaceholder")} style={{ direction: "rtl" }} />
                            </Form.Item>
                          </Col>
                          {enableEnglish && (
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="shortDescription"
                                label={t("eventForm.fields.shortDescriptionLabel")}
                                rules={[{ required: false }]}
                              >
                                <Input placeholder={t("eventForm.fields.shortDescriptionPlaceholder")} />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>

                        <Row gutter={16}>
                          <Col xs={24} md={enableEnglish ? 12 : 24}>
                            <Form.Item
                              name="arabicDescription"
                              label={t("eventForm.fields.arabicDescriptionLabel")}
                              rules={[{ required: true, message: t("eventForm.fields.arabicDescriptionRequired") }]}
                            >
                              <TextArea rows={4} placeholder={t("eventForm.fields.arabicDescriptionPlaceholder")} style={{ direction: "rtl" }} />
                            </Form.Item>
                          </Col>
                          {enableEnglish && (
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="description"
                                label={t("eventForm.fields.descriptionLabel")}
                                rules={[{ required: false }]}
                              >
                                <TextArea rows={4} placeholder={t("eventForm.fields.descriptionPlaceholder")} />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>
                      </>
                    ) : (
                      <>
                        <Form.Item
                          name="title"
                          label={t("eventForm.fields.eventTitleLabel")}
                          rules={[
                            { required: true, message: t("eventForm.fields.eventTitleRequired") },
                          ]}
                        >
                          <Input
                            size="large"
                            placeholder={t("eventForm.fields.eventTitlePlaceholder")}
                          />
                        </Form.Item>

                        <Form.Item
                          name="shortDescription"
                          label={t("eventForm.fields.shortDescriptionLabel")}
                          rules={[
                            {
                              required: true,
                              message: t("eventForm.fields.shortDescriptionRequired"),
                            },
                          ]}
                        >
                          <Input placeholder={t("eventForm.fields.shortDescriptionPlaceholder")} />
                        </Form.Item>

                        <Form.Item
                          name="description"
                          label={t("eventForm.fields.descriptionLabel")}
                          rules={[
                            {
                              required: true,
                              message: t("eventForm.fields.descriptionRequired"),
                            },
                          ]}
                        >
                          <TextArea
                            rows={4}
                            placeholder={t("eventForm.fields.descriptionPlaceholder")}
                          />
                        </Form.Item>

                        <Divider />

                        {/* Arabic Language Toggle */}
                        <Card
                          size="small"
                          style={{ marginBottom: 16, backgroundColor: "#f0f5ff" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Space>
                              <span style={{ fontSize: 16 }}>🌐</span>
                              <div>
                                <strong>{t("eventForm.sections.arabicContent")}</strong>
                                <div style={{ fontSize: 12, color: "#666" }}>
                                  {t("eventForm.sections.arabicContentHint")}
                                </div>
                              </div>
                            </Space>
                            <Switch
                              checked={enableArabic}
                              onChange={(checked) => {
                                setEnableArabic(checked);
                                if (!checked) {
                                  // Clear Arabic fields when disabled
                                  form.setFieldsValue({
                                    arabicTitle: undefined,
                                    arabicShortDescription: undefined,
                                    arabicDescription: undefined,
                                    arabicLocation: undefined,
                                    arabicVenue: undefined,
                                  });
                                }
                              }}
                              checkedChildren={t("eventForm.enabled")}
                              unCheckedChildren={t("eventForm.disabled")}
                            />
                          </div>
                        </Card>

                        {/* Arabic Content Fields - Show only when enabled */}
                        {enableArabic && (
                          <Card
                            title={t("eventForm.sections.arabicContentCardTitle")}
                            size="small"
                            style={{ marginBottom: 16, direction: "rtl" }}
                          >
                            <Form.Item
                              name="arabicTitle"
                              label={t("eventForm.fields.arabicTitleLabel")}
                              rules={[
                                {
                                  required: enableArabic,
                                  message: t("eventForm.fields.arabicTitleRequired"),
                                },
                              ]}
                            >
                              <Input
                                placeholder={t("eventForm.fields.arabicTitlePlaceholderEvent")}
                                style={{ direction: "rtl" }}
                              />
                            </Form.Item>

                            <Form.Item
                              name="arabicShortDescription"
                              label={t("eventForm.fields.arabicShortDescriptionLabel")}
                            >
                              <Input
                                placeholder={t("eventForm.fields.arabicShortDescriptionPlaceholder")}
                                style={{ direction: "rtl" }}
                              />
                            </Form.Item>

                            <Form.Item
                              name="arabicDescription"
                              label={t("eventForm.fields.arabicDescriptionLabel")}
                              rules={[
                                {
                                  required: enableArabic,
                                  message: t("eventForm.fields.arabicDescriptionRequired"),
                                },
                              ]}
                            >
                              <TextArea
                                rows={4}
                                placeholder={t("eventForm.fields.arabicDescriptionPlaceholder")}
                                style={{ direction: "rtl" }}
                              />
                            </Form.Item>

                            <Form.Item
                              name="arabicLocation"
                              label={t("eventForm.fields.arabicLocationLabel")}
                            >
                              <Input
                                placeholder={t("eventForm.fields.arabicLocationPlaceholder")}
                                style={{ direction: "rtl" }}
                              />
                            </Form.Item>

                            <Form.Item name="arabicVenue" label={t("eventForm.fields.arabicVenueLabel")}>
                              <Input
                                placeholder={t("eventForm.fields.arabicVenuePlaceholder")}
                                style={{ direction: "rtl" }}
                              />
                            </Form.Item>
                          </Card>
                        )}
                      </>
                    )}

                    {!isMarriageEvent && (
                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="eventType"
                            label={t("eventForm.fields.eventTypeLabel")}
                            rules={[
                              {
                                required: true,
                                message: t("eventForm.fields.eventTypeRequired"),
                              },
                            ]}
                          >
                            <Select size="large">
                              <Option value="conference">{t("eventForm.eventTypeOptions.conference")}</Option>
                              <Option value="workshop">{t("eventForm.eventTypeOptions.workshop")}</Option>
                              <Option value="seminar">{t("eventForm.eventTypeOptions.seminar")}</Option>
                              <Option value="exhibition">{t("eventForm.eventTypeOptions.exhibition")}</Option>
                              <Option value="networking">{t("eventForm.eventTypeOptions.networking")}</Option>
                              <Option value="webinar">{t("eventForm.eventTypeOptions.webinar")}</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="category"
                            label={t("eventForm.fields.categoryLabel")}
                            rules={[
                              {
                                required: true,
                                message: t("eventForm.fields.categoryRequired"),
                              },
                            ]}
                          >
                            <Select size="large">
                              <Option value="technology">{t("eventForm.categoryOptions.technology")}</Option>
                              <Option value="business">{t("eventForm.categoryOptions.business")}</Option>
                              <Option value="healthcare">{t("eventForm.categoryOptions.healthcare")}</Option>
                              <Option value="education">{t("eventForm.categoryOptions.education")}</Option>
                              <Option value="finance">{t("eventForm.categoryOptions.finance")}</Option>
                              <Option value="marketing">{t("eventForm.categoryOptions.marketing")}</Option>
                              <Option value="other">{t("eventForm.categoryOptions.other")}</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {/* Tags are stored but no feature reads them yet; hidden (kept registered so existing tags survive edits). */}
                    <Form.Item name="tags" hidden />

                  </Card>

                  <Card title={t("eventForm.sections.dateTime")} className="mb-6 fc-compact-card">
                    <style>{COMPACT_CARD_CSS}</style>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="startDate"
                          label={t("eventForm.fields.startDateLabel")}
                          rules={[
                            {
                              required: true,
                              message: t("eventForm.fields.startDateRequired"),
                            },
                          ]}
                        >
                          <EventStartDateInput showAdjustment />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="endDate"
                          label={t("eventForm.fields.endDateLabel")}
                          rules={[
                            {
                              required: true,
                              message: t("eventForm.fields.endDateRequired"),
                            },
                            endDateRule(t),
                          ]}
                          dependencies={["startDate"]}
                        >
                          <EventEndDateInput />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={12}>
                        <Form.Item
                          name="startTime"
                          label={t("eventForm.fields.startTimeLabel")}
                          rules={[
                            {
                              required: true,
                              message: t("eventForm.fields.startTimeRequired"),
                            },
                          ]}
                        >
                          <TimeField defaultTime="21:00" />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={12}>
                        <Form.Item name="endTime" label={t("eventForm.fields.endTimeLabel")} rules={[endTimeRule(t)]} dependencies={["startDate", "endDate", "startTime"]}>
                          <EventEndTimeInput defaultTime="23:30" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  <Card title={t("eventForm.sections.locationDetails")} className="mb-6">
                    {/* Google Maps Location Picker - first so the fields below are auto-filled */}
                    <Form.Item
                      name="latitude"
                      required
                      dependencies={["longitude"]}
                      rules={locationPinRules}
                      label={t("eventForm.fields.selectLocationOnMap")}
                    >
                      <LocationPicker
                        apiKey={"AIzaSyB9dtJwoDszhkMZ2CeD8hR-S-C06CmrfEE"}
                        locationLink={null}
                        initialPosition={savedMapPosition}
                        initialAddress={form.getFieldValue("address") || ""}
                        onLocationSelect={handleLocationSelect}
                        onSearchTextChange={handleMapSearchTextChange}
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        {t("eventForm.fields.mapAutoFillHintAbove")}
                      </p>
                    </Form.Item>

                    <Row gutter={16}>
                      {isMarriageEvent && (
                        <Col xs={24} md={enableEnglish ? 12 : 24}>
                          <Form.Item name="arabicLocation" label={t("eventForm.fields.arabicLocationLabel")} rules={[{ required: true, message: t("eventForm.fields.arabicLocationRequired") }]}>
                            <Input size="large" dir="rtl" placeholder={t("eventForm.fields.arabicLocationPlaceholder")} style={{ direction: "rtl" }} />
                          </Form.Item>
                        </Col>
                      )}
                      {(!isMarriageEvent || enableEnglish) && (
                        <Col xs={24} md={isMarriageEvent ? 12 : 24}>
                          <Form.Item
                            name="location"
                            label={t("eventForm.fields.locationLabel")}
                            rules={[
                              { required: !isMarriageEvent, message: t("eventForm.fields.locationRequired") },
                            ]}
                          >
                            <Input
                              size="large"
                              placeholder={t("eventForm.fields.locationPlaceholder")}
                            />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>

                    <Row gutter={16}>
                      <Col xs={24} md={isMarriageEvent ? 12 : 24}>
                        <Form.Item
                          name="venue"
                          label={t("eventForm.fields.venueNameLabel")}
                          rules={[
                            { required: true, message: t("eventForm.fields.venueNameRequired") },
                          ]}
                        >
                          <Input
                            size="large"
                            placeholder={t("eventForm.fields.venueNamePlaceholder")}
                          />
                        </Form.Item>
                      </Col>
                      {isMarriageEvent && (
                        <Col xs={24} md={12}>
                          <Form.Item name="arabicVenue" label={t("eventForm.fields.arabicVenueLabel")}>
                            <Input size="large" dir="rtl" placeholder={t("eventForm.fields.arabicVenuePlaceholder")} style={{ direction: "rtl" }} />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>

                    <Form.Item name="address" label={t("eventForm.fields.addressLabel")}>
                      <TextArea rows={2} placeholder={t("eventForm.fields.addressPlaceholder")} />
                    </Form.Item>
                    {/* Latitude is owned by the map field above; longitude stays hidden */}
                    <Form.Item name="longitude" hidden>
                      <Input />
                    </Form.Item>
                  </Card>
                </Col>

                <Col xs={24} lg={8}>
                  <div className="overflow-hidden">
                    <Card title={t("eventForm.sections.eventSettings")} className="mb-6">
                      <Form.Item
                        name="capacity"
                        label={isMarriageEvent ? t("eventForm.fields.invitationLimitLabel") : t("eventForm.fields.maximumCapacityLabel")}
                        rules={isMarriageEvent ? [] : [
                          { required: true, message: t("eventForm.fields.capacityRequired") },
                        ]}
                        extra={isMarriageEvent && isVirtual
                          ? (weddingEntitlementLoading
                              ? t("eventForm.fields.checkingWeddingPackage")
                              : weddingEntitlement?.maxInvitations > 0
                                ? t("eventForm.fields.weddingPackageLimitNote")
                                : t("eventForm.fields.noWeddingPackageAvailable"))
                          : undefined}
                      >
                        <InputNumber
                          size="large"
                          style={{ width: "100%" }}
                          min={1}
                          placeholder={isMarriageEvent && isVirtual ? t("eventForm.fields.pendingWeddingPackage") : t("eventForm.fields.capacityPlaceholder")}
                          disabled={isMarriageEvent && isVirtual}
                        />
                      </Form.Item>

                      <Form.Item
                        name="registrationOpen"
                        label={t("eventForm.fields.registrationStatusLabel")}
                        valuePropName="checked"
                      >
                        <Switch
                          checkedChildren={t("eventForm.fields.open")}
                          unCheckedChildren={t("eventForm.fields.closed")}
                          style={{
                            backgroundColor: form.getFieldValue(
                              "registrationOpen"
                            )
                              ? primaryColor
                              : undefined,
                          }}
                        />
                      </Form.Item>

                      {!isMarriageEvent && (
                        <Form.Item
                          name="registrationDeadline"
                          label={t("eventForm.fields.registrationDeadlineLabel")}
                        >
                          <DateField />
                        </Form.Item>
                      )}

                      {!isMarriageEvent && (
                        <Form.Item
                          name="showGuestContact"
                          label={t("eventForm.fields.showGuestContactLabel")}
                          valuePropName="checked"
                          extra={t("eventForm.fields.showGuestContactExtra")}
                        >
                          <Switch />
                        </Form.Item>
                      )}
                    </Card>

                    <Card title={t("eventForm.sections.backgroundImage")} className="mb-6">
                      <p className="text-xs text-gray-500 mb-3">{t("eventForm.upload.backgroundHintEvent")}</p>
                      <div className="text-center">
                        {previewImage && (
                          <div className="mb-4">
                            <img
                              src={previewImage}
                              alt={t("eventForm.altText.preview")}
                              className="w-full h-32 object-cover rounded"
                            />
                          </div>
                        )}
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          customRequest={handleImageUpload}
                          loading={uploading}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={uploading}
                            block
                          >
                            {previewImage
                              ? t("eventForm.upload.changeImage")
                              : t("eventForm.upload.uploadBackground")}
                          </Button>
                        </Upload>
                      </div>

                      {/* Background image is always used as the hero banner; the value is forced to true on load/save. */}
                      <Form.Item name="useBackgroundAsHero" hidden initialValue={true}><Switch /></Form.Item>
                    </Card>

                    <Card title={t("eventForm.sections.invitationImage")} className="mb-6">
                        <p className="text-xs text-gray-500 mb-3">
                          {isMarriageEvent ? t("eventForm.upload.invitationImageHint") : t("eventForm.upload.invitationImageHintEvent")}
                        </p>
                        <div className="text-center">
                          {invitationImagePreview && (
                            <div className="mb-4">
                              <img
                                src={invitationImagePreview}
                                alt={t("eventForm.altText.invitationPreview")}
                                className="w-full h-32 object-cover rounded"
                              />
                            </div>
                          )}
                          <Upload
                            accept="image/*"
                            showUploadList={false}
                            customRequest={handleInvitationImageUpload}
                            loading={invitationImageUploading}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              loading={invitationImageUploading}
                              block
                            >
                              {invitationImagePreview
                                ? t("eventForm.upload.changeInvitationImage")
                                : t("eventForm.upload.uploadInvitationImage")}
                            </Button>
                          </Upload>
                        </div>
                      </Card>

                    <Card title={t("eventForm.sections.digitalCardImage")} className="mb-6">
                      <DigitalCardImageEditor
                        value={passImagePreview}
                        onChange={setPassImagePreview}
                        onUpload={uploadPassImage}
                        uploading={passImageUploading}
                        preview={passPreviewData()}
                      />
                    </Card>

                    <Card title={t("eventForm.sections.eventLogo")} className="mb-6">
                      <div className="text-center">
                        <div className="text-xs text-gray-500 mb-4 px-2">
                          {t("eventForm.upload.logoCompatibilityHint")}
                        </div>
                        {logoPreviewImage && (
                          <div className="mb-4 flex justify-center">
                            <img
                              src={logoPreviewImage}
                              alt={t("eventForm.altText.logoPreview")}
                              className="w-32 h-32 object-contain rounded bg-gray-50 border border-gray-200"
                            />
                          </div>
                        )}
                        <Upload
                          accept="image/png,image/jpeg"
                          showUploadList={false}
                          customRequest={handleLogoUpload}
                          loading={logoUploading}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={logoUploading}
                            block
                          >
                            {logoPreviewImage
                              ? t("eventForm.upload.changeLogo")
                              : t("eventForm.upload.uploadEventLogo")}
                          </Button>
                        </Upload>
                      </div>
                    </Card>

                    <Card title={t("eventForm.sections.walletPassColors")} className="mb-6">
                      <div className="space-y-4">
                        <Form.Item name="regularPassColor" label={t("eventForm.fields.regularPassColorLabel")} className="mb-2">
                          <input type="color" className="w-full h-10 cursor-pointer rounded border border-gray-300" />
                        </Form.Item>
                        {enableVipFeature && (
                          <Form.Item name="vipPassColor" label={t("eventForm.fields.vipPassColorLabel")} className="mb-2">
                            <input type="color" className="w-full h-10 cursor-pointer rounded border border-gray-300" />
                          </Form.Item>
                        )}
                        {enableSpeakersFeature && (
                          <Form.Item name="speakerPassColor" label={t("eventForm.fields.speakerPassColorLabel")} className="mb-2">
                            <input type="color" className="w-full h-10 cursor-pointer rounded border border-gray-300" />
                          </Form.Item>
                        )}
                        {enableExhibitorsFeature && (
                          <Form.Item name="exhibitorPassColor" label={t("eventForm.fields.exhibitorPassColorLabel")} className="mb-2">
                            <input type="color" className="w-full h-10 cursor-pointer rounded border border-gray-300" />
                          </Form.Item>
                        )}
                      </div>
                    </Card>

                    {/* Gate Management - tucked behind Advanced since most organizers,
                        especially those creating their first wedding, never need
                        multiple check-in gates. */}
                    <Collapse
                      className="mb-6"
                      ghost={false}
                      activeKey={gatesOpen ? ["advanced"] : []}
                      onChange={(keys) => setGatesOpen(keys.length > 0)}
                    >
                      <Panel
                        header={
                          <div>
                            <strong>{t("eventForm.sections.entryGates")}</strong>
                            <div style={{ fontSize: 12, color: "#666", fontWeight: "normal" }}>
                              {t("eventForm.sections.entryGatesHint")}
                            </div>
                          </div>
                        }
                        key="advanced"
                      >
                        <div className="responsive-table-container">
                          <GateManagement
                            eventId={id}
                            gates={gates}
                            onChange={handleGatesChange}
                            users={users}
                          />
                        </div>
                      </Panel>
                    </Collapse>

                    {/* Organizer Management */}
                    <div className="mb-6 responsive-table-container">
                      <OrganizerManagement
                        eventId={id}
                        isNewEvent={!id}  // if event is new
                        organizers={organizers}
                        onChange={setOrganizers}
                        onPendingInvitesChange={setPendingOrganizerInvites}  // Handle pending invites
                        hasGates={gates.length > 0}
                        offersGuestContact={isMarriageEvent || !!showGuestContact}
                        users={users}
                      />
                    </div>

                    {/* Speaker Management */}
                    {/* Feature Toggles - Replace SpeakerManagement */}
                    {!isMarriageEvent && (
                      <Card title={t("eventForm.sections.eventFeatures")} className="mb-4">
                        <div className="space-y-4">
                          <div>
                            <Form.Item
                              name="enableVipRegistrations"
                              label={t("eventForm.fields.enableVipLabel")}
                              valuePropName="checked"
                              className="mb-2"
                            >
                              <Switch />
                            </Form.Item>
                            <p className="text-sm text-gray-500 ml-0 mt-1">
                              {t("eventForm.fields.enableVipHint")}
                            </p>
                          </div>
                          <div>
                            <Form.Item
                              name="enableSpeakers"
                              label={t("eventForm.fields.enableSpeakersLabel")}
                              valuePropName="checked"
                              className="mb-2"
                            >
                              <Switch />
                            </Form.Item>
                            <p className="text-sm text-gray-500 ml-0 mt-1">
                              {t("eventForm.fields.enableSpeakersHint")}
                            </p>
                          </div>
                          <div>
                            <Form.Item
                              name="enableExhibitors"
                              label={t("eventForm.fields.enableExhibitorsLabel")}
                              valuePropName="checked"
                              className="mb-2"
                            >
                              <Switch />
                            </Form.Item>
                            <p className="text-sm text-gray-500 ml-0 mt-1">
                              {t("eventForm.fields.enableExhibitorsHint")}
                            </p>
                          </div>
                        </div>
                      </Card>
                    )}

                    {/* Speaker Management Section */}
                    {!isMarriageEvent && enableSpeakersFeature && (
                      <div className="mb-6 responsive-table-container">
                        <SpeakerManagement
                          eventId={id}
                          speakers={speakers}               // <--- Pass State
                          isCreateMode={isCreateMode}
                          onSpeakersChange={setSpeakers}
                          onUpdate={() => fetchEvent()}
                        />
                      </div>
                    )}

                    {/* Exhibitor Management Section */}
                    {!isMarriageEvent && enableExhibitorsFeature && (
                      <div className="mb-6 responsive-table-container">
                        <ExhibitorManagement
                          eventId={id}
                          exhibitors={exhibitors}           // <--- Pass State
                          isCreateMode={isCreateMode}
                          onExhibitorsChange={setExhibitors}
                          onUpdate={() => fetchEvent()}
                        />
                      </div>
                    )}

                    {/* Session Management (Sessions/Agenda) - general-event only */}
                    {!isMarriageEvent && (
                      <div className="mb-6 responsive-table-container">
                        <SessionManagement
                          sessions={sessions}
                          setSessions={setSessions}
                          speakers={speakers}
                        />
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              )}

              {isWizardMode && (
                <div className="mb-6">
                  {/* Step 0: Couple Details */}
                  <div style={{ display: currentStep === 0 ? "block" : "none" }}>
                    <Card title={t("eventForm.wizardSteps.coupleDetails")} className="mb-6">
                      <Card size="small" style={{ marginBottom: 8, backgroundColor: "#f0f5ff" }}
                          styles={{ body: { padding: "8px 12px" } }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Space>
                            <span style={{ fontSize: 16 }}>🌐</span>
                            <div>
                              <strong>{t("eventForm.sections.englishContent")}</strong>
                              <div style={{ fontSize: 11, color: "#666" }}>
                                {t("eventForm.sections.englishContentHint")}
                              </div>
                            </div>
                          </Space>
                          <Switch size="small"
                            checked={enableEnglish}
                            onChange={(checked) => {
                              setEnableEnglish(checked);
                              if (!checked) {
                                form.setFieldsValue({
                                  title: undefined,
                                  groomName: undefined,
                                  brideName: undefined,
                                  shortDescription: undefined,
                                  description: undefined,
                                });
                              }
                            }}
                          />
                        </div>
                      </Card>

                      <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: "8px 12px" } }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <div>
                            <strong>{t("eventForm.fields.weddingOccasionLabel")}</strong>
                            <div style={{ fontSize: 11, color: "#666" }}>
                              {t("eventForm.fields.weddingOccasionHint")}
                            </div>
                          </div>
                          <Switch size="small"
                            checked={isWeddingOccasion}
                            onChange={(checked) => {
                              setIsWeddingOccasion(checked);
                              if (!checked) {
                                form.setFieldsValue({
                                  groomName: undefined,
                                  brideName: undefined,
                                  arabicGroomName: undefined,
                                  arabicBrideName: undefined,
                                });
                              }
                            }}
                          />
                        </div>
                      </Card>

                      <Row gutter={16}>
                        <Col xs={24} md={enableEnglish ? 12 : 24}>
                          <Form.Item name="arabicTitle" label={t("eventForm.fields.arabicTitleLabel")} rules={[{ required: true, message: t("eventForm.fields.arabicTitleRequired") }]}>
                            <Input size="large" dir="rtl" placeholder={t("eventForm.fields.arabicTitlePlaceholderWedding")} style={{ direction: "rtl" }} />
                          </Form.Item>
                        </Col>
                        {enableEnglish && (
                          <Col xs={24} md={12}>
                            <Form.Item name="title" label={t("eventForm.fields.weddingTitleLabel")} rules={[{ required: false }]}>
                              <Input size="large" placeholder={t("eventForm.fields.weddingTitlePlaceholder")} />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>

                      {isWeddingOccasion && (
                        <>
                      <Row gutter={16}>
                        <Col xs={24} md={enableEnglish ? 12 : 24}>
                          <Form.Item name="arabicGroomName" label={t("eventForm.fields.arabicGroomNameLabel")} rules={[{ required: true, message: t("eventForm.fields.arabicGroomNameRequired") }]}>
                            <Input dir="rtl" placeholder={t("eventForm.fields.arabicGroomNamePlaceholder")} style={{ direction: "rtl" }} />
                          </Form.Item>
                        </Col>
                        {enableEnglish && (
                          <Col xs={24} md={12}>
                            <Form.Item name="groomName" label={t("eventForm.fields.groomNameLabel")} rules={[{ required: false }]}>
                              <Input placeholder={t("eventForm.fields.groomNamePlaceholder")} />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>

                      <Row gutter={16}>
                        <Col xs={24} md={enableEnglish ? 12 : 24}>
                          <Form.Item name="arabicBrideName" label={t("eventForm.fields.arabicBrideNameLabel")} rules={[{ required: true, message: t("eventForm.fields.arabicBrideNameRequired") }]}>
                            <Input dir="rtl" placeholder={t("eventForm.fields.arabicBrideNamePlaceholder")} style={{ direction: "rtl" }} />
                          </Form.Item>
                        </Col>
                        {enableEnglish && (
                          <Col xs={24} md={12}>
                            <Form.Item name="brideName" label={t("eventForm.fields.brideNameLabel")} rules={[{ required: false }]}>
                              <Input placeholder={t("eventForm.fields.brideNamePlaceholder")} />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                        </>
                      )}

                      <Row gutter={16}>
                        <Col xs={24} md={enableEnglish ? 12 : 24}>
                          <Form.Item name="arabicShortDescription" label={t("eventForm.fields.arabicShortDescriptionLabel")} rules={[{ required: true, message: t("eventForm.fields.arabicShortDescriptionRequired") }]}>
                            <Input dir="rtl" placeholder={t("eventForm.fields.arabicShortDescriptionPlaceholder")} style={{ direction: "rtl" }} />
                          </Form.Item>
                        </Col>
                        {enableEnglish && (
                          <Col xs={24} md={12}>
                            <Form.Item name="shortDescription" label={t("eventForm.fields.shortDescriptionLabel")} rules={[{ required: false }]}>
                              <Input placeholder={t("eventForm.fields.shortDescriptionPlaceholder")} />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>

                      <Row gutter={16}>
                        <Col xs={24} md={enableEnglish ? 12 : 24}>
                          <Form.Item name="arabicDescription" label={t("eventForm.fields.arabicDescriptionLabel")} rules={[{ required: true, message: t("eventForm.fields.arabicDescriptionRequired") }]}>
                            <TextArea rows={4} placeholder={t("eventForm.fields.arabicDescriptionPlaceholder")} style={{ direction: "rtl" }} />
                          </Form.Item>
                        </Col>
                        {enableEnglish && (
                          <Col xs={24} md={12}>
                            <Form.Item name="description" label={t("eventForm.fields.descriptionLabel")} rules={[{ required: false }]}>
                              <TextArea rows={4} placeholder={t("eventForm.fields.descriptionPlaceholder")} />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>

                      <Form.Item name="invitedBy" label={t("eventForm.fields.invitedByLabel")}>
                        <Input placeholder={t("eventForm.fields.invitedByPlaceholder")} />
                      </Form.Item>
                    </Card>
                  </div>

                  {/* Step 1: Date & Venue */}
                  <div style={{ display: currentStep === 1 ? "block" : "none" }}>
                      <Card title={t("eventForm.sections.dateTime")} className="mb-6 fc-compact-card">
                    <style>{COMPACT_CARD_CSS}</style>
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Form.Item name="startDate" label={t("eventForm.fields.startDateLabel")} rules={[{ required: true, message: t("eventForm.fields.startDateRequired") }]}>
                              <EventStartDateInput showAdjustment />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item name="endDate" label={t("eventForm.fields.endDateLabel")} rules={[{ required: true, message: t("eventForm.fields.endDateRequired") }, endDateRule(t)]} dependencies={["startDate"]}>
                              <EventEndDateInput />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={12}>
                            <Form.Item name="startTime" label={t("eventForm.fields.startTimeLabel")} rules={[{ required: true, message: t("eventForm.fields.startTimeRequired") }]}>
                              <TimeField defaultTime="21:00" />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={12}>
                            <Form.Item name="endTime" label={t("eventForm.fields.endTimeLabel")} rules={[endTimeRule(t)]} dependencies={["startDate", "endDate", "startTime"]}>
                              <EventEndTimeInput defaultTime="23:30" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>

                      <Card title={t("eventForm.sections.venue")} className="mb-6 fc-compact-card">
                        <style>{COMPACT_CARD_CSS}</style>
                        <Form.Item
                          name="latitude"
                          required
                          dependencies={["longitude"]}
                          rules={locationPinRules}
                          label={t("eventForm.fields.selectLocationOnMap")}
                        >
                          {/* "Use my current location" hidden for now per request - handleUseCurrentLocation
                              stays wired up in case it's re-enabled later. */}
                          <LocationPicker
                            compact
                            apiKey={"AIzaSyB9dtJwoDszhkMZ2CeD8hR-S-C06CmrfEE"}
                            locationLink={null}
                            initialPosition={savedMapPosition}
                            initialAddress={form.getFieldValue("address") || ""}
                            onLocationSelect={handleLocationSelect}
                            onSearchTextChange={handleMapSearchTextChange}
                          />
                        </Form.Item>
                        <Form.Item name="longitude" hidden><Input /></Form.Item>

                        {/* Full address is auto-filled from the map pick and kept visible/editable here */}
                        <Form.Item name="address" label={t("eventForm.fields.addressLabel")}>
                          <TextArea rows={2} placeholder={t("eventForm.fields.addressPlaceholder")} />
                        </Form.Item>

                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                          <Switch
                            size="small"
                            checked={showEnglishLocation}
                            onChange={(checked) => {
                              setShowEnglishLocation(checked);
                              scheduleWeddingDraftSave();
                            }}
                          />
                          <span style={{ fontSize: 13 }}>{t("eventForm.fields.addEnglishLocation")}</span>
                        </div>

                        <Row gutter={16}>
                          <Col xs={24} md={showEnglishLocation ? 12 : 24}>
                            <Form.Item name="arabicLocation" label={t("eventForm.fields.arabicLocationLabel")} rules={[{ required: true, whitespace: true, message: t("eventForm.fields.arabicLocationRequired") }]}>
                              <Input size="large" dir="rtl" placeholder={t("eventForm.fields.arabicLocationPlaceholder")} style={{ direction: "rtl" }} />
                            </Form.Item>
                          </Col>
                          {showEnglishLocation && (
                            <Col xs={24} md={12}>
                              <Form.Item name="location" label={t("eventForm.fields.locationLabel")} rules={[{ required: true, whitespace: true, message: t("eventForm.fields.locationRequired") }]}>
                                <Input size="large" placeholder={t("eventForm.fields.locationPlaceholder")} />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>

                        <Row gutter={16}>
                          <Col xs={24} md={showEnglishLocation ? 12 : 24}>
                            <Form.Item name="arabicVenue" label={t("eventForm.fields.arabicVenueLabel")} rules={[{ required: true, whitespace: true, message: t("eventForm.fields.arabicVenueRequired") }]}>
                              <Input size="large" dir="rtl" placeholder={t("eventForm.fields.arabicVenuePlaceholder")} style={{ direction: "rtl" }} />
                            </Form.Item>
                          </Col>
                          {showEnglishLocation && (
                            <Col xs={24} md={12}>
                              <Form.Item name="venue" label={t("eventForm.fields.venueNameLabel")} rules={[{ required: true, whitespace: true, message: t("eventForm.fields.venueNameRequired") }]}>
                                <Input size="large" placeholder={t("eventForm.fields.venueNamePlaceholder")} />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>
                      </Card>
                  </div>

                  {/* Step 2: Photos & Branding */}
                  <div style={{ display: currentStep === 2 ? "block" : "none" }}>
                      <Card title={t("eventForm.sections.backgroundImage")} className="mb-6">
                        <p className="text-xs text-gray-500 mb-3">{t("eventForm.upload.backgroundHintWedding")}</p>
                        <div className="text-center">
                          {previewImage && (
                            <div className="mb-4">
                              <img src={previewImage} alt={t("eventForm.altText.preview")} className="w-full h-32 object-cover rounded" />
                            </div>
                          )}
                          <Upload accept="image/*" showUploadList={false} customRequest={handleImageUpload} loading={uploading}>
                            <Button icon={<UploadOutlined />} loading={uploading} block>
                              {previewImage ? t("eventForm.upload.changeImage") : t("eventForm.upload.uploadBackground")}
                            </Button>
                          </Upload>
                        </div>
                        <Form.Item name="useBackgroundAsHero" hidden initialValue={true}><Switch /></Form.Item>
                      </Card>

                      <Card title={t("eventForm.sections.invitationImage")} className="mb-6">
                        <p className="text-xs text-gray-500 mb-3">{t("eventForm.upload.invitationImageHint")}</p>
                        <div className="text-center">
                          {invitationImagePreview && (
                            <div className="mb-4">
                              <img src={invitationImagePreview} alt={t("eventForm.altText.invitationPreview")} className="w-full h-32 object-cover rounded" />
                            </div>
                          )}
                          <Upload accept="image/*" showUploadList={false} customRequest={handleInvitationImageUpload} loading={invitationImageUploading}>
                            <Button icon={<UploadOutlined />} loading={invitationImageUploading} block>
                              {invitationImagePreview ? t("eventForm.upload.changeInvitationImage") : t("eventForm.upload.uploadInvitationImage")}
                            </Button>
                          </Upload>
                        </div>
                      </Card>

                      <Card title={t("eventForm.invitationTheme.title")} className="mb-6">
                        <Form.Item name="landingTheme" noStyle>
                          <InvitationThemePicker landingSlug={landingSlug} />
                        </Form.Item>
                      </Card>

                      <Card title={t("eventForm.sections.digitalCardImage")} className="mb-6">
                        <DigitalCardImageEditor
                          value={passImagePreview}
                          onChange={setPassImagePreview}
                          onUpload={uploadPassImage}
                          uploading={passImageUploading}
                          preview={{ ...passPreviewData(), hideTicketType: true }}
                        />
                      </Card>

                      <Card title={t("eventForm.sections.eventLogo")} className="mb-6">
                        <div className="text-center">
                          <div className="text-xs text-gray-500 mb-4 px-2">
                            {t("eventForm.upload.logoWalletHintShort")}
                          </div>
                          {logoPreviewImage && (
                            <div className="mb-4 flex justify-center">
                              <img src={logoPreviewImage} alt={t("eventForm.altText.logoPreview")} className="w-32 h-32 object-contain rounded bg-gray-50 border border-gray-200" />
                            </div>
                          )}
                          {isDefaultLogo && (
                            <div className="text-xs text-gray-500 mb-3 px-2">{t("eventForm.upload.defaultWeddingLogoHint")}</div>
                          )}
                          <Upload accept="image/png,image/jpeg" showUploadList={false} customRequest={handleLogoUpload} loading={logoUploading}>
                            <Button icon={<UploadOutlined />} loading={logoUploading} block>
                              {logoPreviewImage ? t("eventForm.upload.changeLogo") : t("eventForm.upload.uploadEventLogo")}
                            </Button>
                          </Upload>
                        </div>
                      </Card>

                      <Card title={t("eventForm.sections.walletPassColor")} className="mb-6">
                        <Form.Item name="regularPassColor" label={t("eventForm.fields.guestPassColorLabel")} className="mb-2">
                          <input type="color" className="w-full h-10 cursor-pointer rounded border border-gray-300" />
                        </Form.Item>
                      </Card>

                      <Card title={t("eventForm.sections.whatsappInvitation")} className="mb-6">
                        <MessageLanguageSetting />

                        {/* The editable lines live in the preview below. These hidden items keep the values in the form
                            so they are saved with the event. */}
                        <Form.Item name="guestTitle" hidden><Input /></Form.Item>
                        <Form.Item name="invitationText" hidden><Input /></Form.Item>
                        <Form.Item name="closingText" hidden><Input /></Form.Item>

                        <Button
                          block
                          icon={<WhatsAppOutlined style={{ color: "#25D366" }} />}
                          onClick={() => setWhatsAppPreviewOpen((open) => !open)}
                        >
                          {whatsAppPreviewOpen ? t("eventForm.whatsapp.previewHide") : t("eventForm.whatsapp.previewShow")}
                        </Button>

                        {whatsAppPreviewOpen && (
                        <div className="mt-4">
                        <WhatsAppInvitationPreview
                          editable
                          titleEditable={previewTitleEditable}
                          displayTitle={previewLanguage === "ar" ? previewArabicTitle : weddingTitleWatch}
                          guestTitle={guestTitleWatch || DEFAULT_GUEST_TITLE}
                          guestTitleOptions={guestTitleOptions}
                          invitationText={invitationTextWatch}
                          closingText={closingTextWatch}
                          defaultInvitationText={defaultInvitationText(weddingInvitedByWatch)}
                          defaultClosingText={DEFAULT_CLOSING_TEXT}
                          eventDate={previewEventDate}
                          invitationImageUrl={invitationImagePreview}
                          onChangeGuestTitle={(value) => setWhatsAppField("guestTitle", value)}
                          onChangeInvitationText={(value) => setWhatsAppField("invitationText", value)}
                          onChangeTitle={onPreviewTitleChange}
                          onChangeClosingText={(value) => setWhatsAppField("closingText", value)}
                          onImageUpload={handleInvitationImageUpload}
                          onEditDate={() => setCurrentStep(1)}
                        />
                        </div>
                        )}
                      </Card>

                      <Card title={t("eventForm.sections.whatsappThankYou")} className="mb-6">
                        {/* Kept in the form so it's saved with the event even while the preview is collapsed.
                            Sending itself is manual (a "Send thank-you messages" action in Event Registrations,
                            for checked-in guests, whenever the organiser is ready) - this only customises the
                            wording, and can also be edited right from Event Registrations without coming here. */}
                        <Form.Item name="thankYouText" hidden><Input /></Form.Item>

                        <Button
                          block
                          icon={<WhatsAppOutlined style={{ color: "#25D366" }} />}
                          onClick={() => setThankYouPreviewOpen((open) => !open)}
                        >
                          {thankYouPreviewOpen ? t("eventForm.whatsapp.previewHide") : t("eventForm.whatsapp.previewShow")}
                        </Button>

                        {thankYouPreviewOpen && (
                          <div className="mt-4">
                            <ThankYouMessagePreview
                              editable
                              displayTitle={previewLanguage === "ar" ? previewArabicTitle : weddingTitleWatch}
                              thankYouText={thankYouTextWatch}
                              defaultThankYouText={DEFAULT_THANK_YOU_TEXT}
                              onChangeThankYouText={(value) => setWhatsAppField("thankYouText", value)}
                            />
                          </div>
                        )}
                      </Card>
                  </div>

                  {/* Step 3: Guests & Sharing */}
                  <div style={{ display: currentStep === 3 ? "block" : "none" }}>
                      {isVirtual ? (
                        <>
                          {/* The invitation limit comes from the purchased package and cannot be edited, and the guest
                              invitation form already shows it - so no card here. The value stays in the form (hidden)
                              and is still saved. Only a missing package needs the user's attention. */}
                          <Form.Item name="capacity" hidden><InputNumber /></Form.Item>
                          {!weddingEntitlementLoading && !(weddingEntitlement?.maxInvitations > 0) && (
                            <Alert type="warning" showIcon className="mb-6" message={t("eventForm.fields.noWeddingPackageAvailable")} />
                          )}
                        </>
                      ) : (
                      <Card title={t("eventForm.sections.guestsRegistration")} className="mb-6">
                        <Form.Item
                          name="capacity"
                          label={t("eventForm.fields.invitationLimitLabel")}
                          extra={isVirtual
                            ? (weddingEntitlementLoading
                                ? t("eventForm.fields.checkingWeddingPackage")
                                : weddingEntitlement?.maxInvitations > 0
                                  ? t("eventForm.fields.weddingPackageLimitNote")
                                  : t("eventForm.fields.noWeddingPackageAvailable"))
                            : t("eventForm.fields.guestsBasedOnPackage")}
                        >
                          <InputNumber
                            size="large"
                            style={{ width: "100%" }}
                            min={1}
                            placeholder={isVirtual ? t("eventForm.fields.pendingWeddingPackage") : t("eventForm.fields.capacityPlaceholder")}
                            disabled={isVirtual}
                          />
                        </Form.Item>
                        {/* Weddings are private invitations: registration is always on, so no toggle. */}
                      </Card>
                      )}

                      <div className="mb-6 responsive-table-container">
                        <OrganizerManagement
                          eventId={id}
                          isNewEvent={!id}
                          organizers={organizers}
                          onChange={setOrganizers}
                          onPendingInvitesChange={setPendingOrganizerInvites}
                          hasGates={gates.length > 0}
                          users={users}
                          offersGuestContact={isMarriageEvent || !!showGuestContact}
                        />
                      </div>

                      <Collapse
                        className="mb-6"
                        ghost={false}
                        activeKey={gatesOpen ? ["advanced"] : []}
                        onChange={(keys) => setGatesOpen(keys.length > 0)}
                      >
                        <Panel
                          header={
                            <div>
                              <strong>{t("eventForm.sections.entryGates")}</strong>
                              <div style={{ fontSize: 12, color: "#666", fontWeight: "normal" }}>
                                {t("eventForm.sections.entryGatesHint")}
                              </div>
                            </div>
                          }
                          key="advanced"
                        >
                          <div className="responsive-table-container">
                            <GateManagement eventId={id} gates={gates} onChange={handleGatesChange} users={users} />
                          </div>
                        </Panel>
                      </Collapse>
                  </div>

                  {/* Step 4: Review & Create */}
                  {currentStep === 4 && (
                    <Card title={t("eventForm.sections.reviewWeddingEvent")} className="mb-6">
                      {(() => {
                        const values = form.getFieldsValue(true);
                        const dateRange = values.startDate && values.endDate
                          ? `${formatDate(values.startDate, i18n.language, values.calendarPreference, values.hijriAdjustmentDays || 0)} - ${formatDate(values.endDate, i18n.language, values.calendarPreference, values.hijriAdjustmentDays || 0)}`
                          : t("eventForm.review.notSet");
                        return (
                          <div className="space-y-2 text-gray-700">
                            {isWeddingOccasion ? (
                              <p>
                                <strong>{t("eventForm.review.yourWedding")}</strong> {values.groomName || values.arabicGroomName || "?"} &amp; {values.brideName || values.arabicBrideName || "?"}
                                {values.title ? ` — "${values.title}"` : ""}
                              </p>
                            ) : (
                              <p>
                                <strong>{t("eventForm.fields.weddingTitleLabel")}:</strong> {values.title || values.arabicTitle || t("eventForm.review.notSet")}
                              </p>
                            )}
                            <p><strong>{t("eventForm.review.when")}</strong> {dateRange}</p>
                            <p><strong>{t("eventForm.review.where")}</strong> {values.venue || values.arabicVenue || t("eventForm.review.notSet")}{(values.location || values.arabicLocation) ? `, ${values.location || values.arabicLocation}` : ""}</p>
                            <p><strong>{t("eventForm.review.invitedByLabel")}</strong> {values.invitedBy || t("eventForm.review.notSet")}</p>
                            <p><strong>{t("eventForm.review.invitationLimitLabel")}</strong> {values.capacity ? t("eventForm.review.guestsSuffix", { count: values.capacity }) : t("eventForm.fields.pendingWeddingPackage")}</p>
                            <p><strong>{t("eventForm.review.backgroundImageLabel")}</strong> {previewImage ? t("eventForm.review.uploaded") : t("eventForm.review.notUploaded")}</p>
                            <p><strong>{t("eventForm.review.invitationImageLabel")}</strong> {invitationImagePreview ? t("eventForm.review.uploaded") : t("eventForm.review.notUploaded")}</p>
                            <p><strong>{t("eventForm.review.eventLogoLabel")}</strong> {logoPreviewImage ? t("eventForm.review.uploaded") : t("eventForm.review.notUploaded")}</p>
                            <p>
                              <strong>{t("eventForm.review.organizersAdded")}</strong> {organizers.length + pendingOrganizerInvites.length}
                              {pendingOrganizerInvites.length > 0 && (
                                <span className="text-orange-500 ml-1">
                                  ({t("eventForm.review.organizersPendingSuffix", { count: pendingOrganizerInvites.length })})
                                </span>
                              )}
                            </p>
                            <p><strong>{t("eventForm.review.gatesConfigured")}</strong> {gates.length}</p>
                          </div>
                        );
                      })()}
                      <p className="text-xs text-gray-500 mt-4">
                        {t("eventForm.review.footerNote")}
                      </p>
                    </Card>
                  )}

                  <Card className="mt-6 form-actions-card">
                    <div className="flex flex-col gap-3">
                      {/* Editing: "Update Event" gets its own full-width row on phones. */}
                      {isEditing && currentStep < WIZARD_STEPS.length - 1 && (
                        <div className="sm:hidden">
                          <Button
                            size="large"
                            block
                            icon={<SaveOutlined />}
                            onClick={() => form.submit()}
                            loading={loading}
                            disabled={weddingEntitlementLoading}
                          >
                            {t("eventForm.buttons.updateEvent")}
                          </Button>
                        </div>
                      )}
                      <div className="flex gap-3 sm:justify-between">
                        <Button
                          size="large"
                          className="flex-1 sm:flex-none"
                          style={{ minWidth: 120 }}
                          onClick={currentStep === 0 ? handleCancel : goToPreviousStep}
                        >
                          {currentStep === 0 ? (
                            t("eventForm.buttons.cancel")
                          ) : (
                            <span className="inline-flex items-center justify-center gap-2">
                              {i18n.dir() === "rtl" ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
                              {t("eventForm.buttons.back")}
                            </span>
                          )}
                        </Button>
                        {currentStep < WIZARD_STEPS.length - 1 ? (
                          <div className="flex flex-1 gap-3 sm:flex-none">
                            {isEditing && (
                              <div className="hidden sm:block">
                                <Button
                                  size="large"
                                  icon={<SaveOutlined />}
                                  onClick={() => form.submit()}
                                  loading={loading}
                                  disabled={weddingEntitlementLoading}
                                >
                                  {t("eventForm.buttons.updateEvent")}
                                </Button>
                              </div>
                            )}
                            <Button
                              type="primary"
                              size="large"
                              className="flex-1 sm:flex-none"
                              style={{ minWidth: 120, backgroundColor: primaryColor, borderColor: primaryColor }}
                              onClick={goToNextStep}
                            >
                              <span className="inline-flex items-center justify-center gap-2">
                                {t("eventForm.buttons.next")}
                                {i18n.dir() === "rtl" ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
                              </span>
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="primary"
                            size="large"
                            className="flex-1 sm:flex-none"
                            icon={<SaveOutlined />}
                            onClick={() => form.submit()}
                            loading={loading}
                            disabled={weddingEntitlementLoading}
                            style={{ minWidth: 120, backgroundColor: primaryColor, borderColor: primaryColor }}
                          >
                            {isEditing ? t("eventForm.buttons.updateEvent") : t("eventForm.buttons.createEvent")}
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 text-center text-gray-500 text-sm">
                      {t("eventForm.needHelp")}{" "}
                      <a
                        href="https://future-horses.com/contact/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                      >
                        {t("eventForm.contactSupport")}
                      </a>
                    </div>
                  </Card>
                </div>
              )}

              {!isWizardMode && (
              <Card className="mt-6 form-actions-card">
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <Button size="large" className="w-full sm:w-auto" onClick={handleCancel}>
                    {t("eventForm.buttons.cancel")}
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    className="w-full sm:w-auto"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={loading}
                    disabled={isMarriageEvent && weddingEntitlementLoading}
                    style={{
                      backgroundColor: primaryColor,
                      borderColor: primaryColor,
                    }}
                  >
                    {isEditing ? t("eventForm.buttons.updateEvent") : t("eventForm.buttons.createEvent")}
                  </Button>
                </div>
                <div className="mt-4 text-center text-gray-500 text-sm">
                  {t("eventForm.needHelp")}{" "}
                  <a
                    href="https://future-horses.com/contact/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                  >
                    {t("eventForm.contactSupport")}
                  </a>
                </div>
              </Card>
              )}
            </Form>
          </div>
        </Content>

        {/* Landing Page Preview Modal */}
        <Modal
          title={t("eventForm.landingPreviewTitle")}
          open={previewModalVisible}
          onCancel={() => setPreviewModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setPreviewModalVisible(false)}>
              {t("eventForm.buttons.close")}
            </Button>,
            <Button
              key="open"
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => window.open(previewUrl, "_blank")}
              style={{
                backgroundColor: primaryColor,
                borderColor: primaryColor,
              }}
            >
              {t("eventForm.buttons.openInNewTab")}
            </Button>,
          ]}
          width="90%"
          style={{ top: 20 }}
          bodyStyle={{ padding: 0, height: "calc(100vh - 200px)" }}
        >
          {previewUrl && (
            <iframe
              src={previewUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: "8px",
              }}
              title={t("eventForm.landingPreviewTitle")}
            />
          )}
        </Modal>
      </Layout>
    </>
  );
};

export default EventFormPage;
